import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  Alert,
  Button,
  Form,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  faArrowUpRightFromSquare,
  faArrowsRotate,
  faBuilding,
  faChevronDown,
  faUserPen,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { DarkModeContext } from "../../context/DarkModeContext";
import Footer from "../../components/comunes/Footer";
import TituloPagina from "../../components/comunes/TituloPagina";
import logo from "../../assets/img/logo.png";
import { getToken } from "../../utils/auth";
import { descargarOrdenPagoLegacy } from "../../helpers/legacyOrdenPagoPdf";
import { crmPostventaApi } from "./crmPostventaApi";
import ClienteSelectorModal from "./ClienteSelectorModal";
import "./crm-postventa.css";

const STORAGE_KEY = "crm_postventa_cliente_id";

const makeToday = () => new Date().toISOString().slice(0, 10);
const makeMonthStart = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
};
const makeCurrentMonth = () => makeToday().slice(0, 7);
const normalizeDigits = (value) => String(value || "").replace(/\D/g, "");
const getFirstValue = (item, keys = []) => {
  for (const key of keys) {
    const value = item?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }

  return "";
};
const normalizeLegacyIdentifier = (value) => {
  if (value === undefined || value === null) return "";

  const normalized = String(value).trim();
  if (!normalized) return "";

  return normalized.replace(/\.0+$/g, "");
};
const normalizeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
const formatLegacyDisplayDate = (value) => {
  if (!value) return "-";
  if (/^\d{2}-\d{2}-\d{4}$/.test(String(value))) {
    return String(value);
  }

  const [year, month, day] = String(value).split("-");
  if (!year || !month || !day) {
    return String(value);
  }

  return `${day}-${month}-${year}`;
};
const formatLegacyMonthLabel = (value) => {
  if (!value) return "-";
  const [year, month] = String(value).split("-");
  if (!year || !month) {
    return String(value);
  }

  return `${month}/${String(year).slice(-2)}`;
};
const parseFlexibleAmount = (value) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  let normalized = String(value).trim();
  if (!normalized) {
    return null;
  }

  normalized = normalized.replace(/\$/g, "").replace(/\s/g, "").replace(/[^0-9,.-]/g, "");
  if (!normalized) {
    return null;
  }

  const dotCount = (normalized.match(/\./g) || []).length;
  const commaCount = (normalized.match(/,/g) || []).length;
  const lastComma = normalized.lastIndexOf(",");
  const lastDot = normalized.lastIndexOf(".");
  const hasBoth = lastComma >= 0 && lastDot >= 0;
  const lastSepIndex = Math.max(lastComma, lastDot);

  if (lastSepIndex >= 0) {
    const digitsAfter = normalized.length - lastSepIndex - 1;
    const hasSingleSep = dotCount + commaCount === 1;
    const shouldTreatAsThousandsOnly = hasSingleSep && digitsAfter === 3 && !hasBoth;

    if (shouldTreatAsThousandsOnly) {
      normalized = normalized.replace(/[.,]/g, "");
    } else {
      const integerPart = normalized.slice(0, lastSepIndex).replace(/[.,]/g, "");
      const decimalPart = normalized.slice(lastSepIndex + 1).replace(/[.,]/g, "");
      normalized = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
    }
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};
const parseLegacyDate = (value) => {
  if (!value) return null;

  const raw = String(value).trim();
  if (!raw) return null;

  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const date = new Date(`${raw.slice(0, 10)}T00:00:00`);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const [day, month, year] = raw.split(/[/-]/);
  if (!day || !month || !year) {
    return null;
  }

  const iso = `${year.padStart(4, "20")}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  const date = new Date(`${iso}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};
const formatCurrencyCell = (value) => {
  const parsed = parseFlexibleAmount(value);
  if (parsed === null) {
    return "-";
  }

  return parsed.toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};
const normalizeCuponLegacyRow = (item = {}) => {
  const fechaOperacion = getFirstValue(item, ["fechaOperacion", "FechaOperacion", "fecha operacion", "Fecha Operacion"]);
  const fechaDePago = getFirstValue(item, ["fechaDePago", "FechaDePago", "fecha de pago", "Fecha de Pago"]);
  const nroDeCupon = normalizeLegacyIdentifier(getFirstValue(item, ["nroDeCupon", "NroDeCupon", "Nro# de Cupon"]));
  const nroDeComercio = normalizeLegacyIdentifier(getFirstValue(item, ["nroDeComercio", "NroDeComercio", "nroDeTerminal", "NroDeTerminal", "Nro# de Comercio"]));
  const nroDeTarjeta = normalizeLegacyIdentifier(getFirstValue(item, ["nroDeTarjeta", "NroDeTarjeta", "Nro# de Tarjeta"]));
  const brutoRaw = getFirstValue(item, ["totalBruto", "TotalBruto", "bruto", "Bruto", "importe", "Importe", "Total Bruto"]);
  const totalBruto = parseFlexibleAmount(brutoRaw);
  const cuotas = normalizeLegacyIdentifier(getFirstValue(item, ["cuotas", "Cuotas"]));
  const tarjeta = getFirstValue(item, ["tarjeta", "Tarjeta"]);
  const provincia = getFirstValue(item, ["provincia", "Provincia"]);
  const nombreComercio = getFirstValue(item, ["nombreComercio", "NombreComercio", "Nombre Comercio"]);
  const cuit = normalizeDigits(getFirstValue(item, ["cuit", "Cuit", "CUIT"]));

  return {
    fechaOperacion: formatLegacyDisplayDate(fechaOperacion),
    fechaDePago: formatLegacyDisplayDate(fechaDePago),
    fechaOperacionDate: parseLegacyDate(fechaOperacion),
    fechaDePagoDate: parseLegacyDate(fechaDePago),
    nroDeCupon: nroDeCupon || "-",
    nroDeComercio: nroDeComercio || "-",
    nroDeTarjeta: nroDeTarjeta || "-",
    totalBruto,
    brutoRaw,
    cuotas: cuotas || "-",
    tarjeta: tarjeta || "-",
    provincia: provincia || "-",
    nombreComercio: nombreComercio || "-",
    cuit: cuit || "-",
  };
};
const buildCuponDedupeKey = (item = {}) =>
  [
    normalizeLegacyIdentifier(item.nroDeCupon),
    normalizeLegacyIdentifier(item.nroDeComercio),
    formatLegacyDisplayDate(item.fechaOperacion),
    item.totalBruto === null ? "" : Number(item.totalBruto).toFixed(4),
    normalizeText(item.tarjeta),
  ].join("|");
const buildCuponSummary = (rows = []) => {
  const fechasOperacion = rows.map((item) => item.fechaOperacionDate).filter(Boolean).sort((a, b) => a - b);
  const fechasPago = rows.map((item) => item.fechaDePagoDate).filter(Boolean).sort((a, b) => a - b);
  const totalBruto = rows.reduce((acc, item) => acc + (Number.isFinite(item.totalBruto) ? item.totalBruto : 0), 0);

  const formatDate = (value) => (value ? value.toLocaleDateString("es-AR") : "-");

  return {
    cantidad: rows.length,
    totalBruto,
    fechaOperacionMin: formatDate(fechasOperacion[0]),
    fechaOperacionMax: formatDate(fechasOperacion[fechasOperacion.length - 1]),
    fechaPagoMin: formatDate(fechasPago[0]),
    fechaPagoMax: formatDate(fechasPago[fechasPago.length - 1]),
  };
};
const formatCurrencyDetailed = (value) =>
  Number(value || 0).toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const currency = (value) =>
  typeof value === "number"
    ? value.toLocaleString("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 })
    : "Sin datos";

const percent = (value) =>
  typeof value === "number"
    ? `${(value * 100).toLocaleString("es-AR", { maximumFractionDigits: 2 })}%`
    : "Sin datos";

const GESTION_STATES = ["Pendiente", "En proceso", "Contactado", "Resuelto", "Escalado", "Cerrado", "Descartado"];
const SECTION_PATHS = {
  alertas: "/administracion/crm-postventa/alertas",
  ficha: "/administracion/crm-postventa/ficha",
  facturacion: "/administracion/crm-postventa/facturacion",
  "ordenes-pago": "/administracion/crm-postventa/ordenes-pago",
  cupones: "/administracion/crm-postventa/cupones",
  herramientas: "/administracion/crm-postventa/herramientas",
  gestion: "/administracion/crm-postventa/gestion",
  seguimiento: "/administracion/crm-postventa/seguimiento",
};

const toneForGestionState = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized.includes("resuelto") || normalized.includes("cerrado")) return "success";
  if (normalized.includes("escalado")) return "danger";
  if (normalized.includes("contactado") || normalized.includes("proceso")) return "warning";
  if (normalized.includes("pendiente")) return "default";
  return "default";
};

const toneForPriority = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized.includes("cr")) return "danger";
  if (normalized.includes("alta")) return "warning";
  if (normalized.includes("media")) return "default";
  return "success";
};

const toneForSegment = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "a") return "danger";
  if (normalized === "b") return "warning";
  if (normalized === "c") return "default";
  return "success";
};

const toneForPattern = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized.includes("diario")) return "danger";
  if (normalized.includes("fin_de_semana")) return "warning";
  if (normalized.includes("intermitente")) return "default";
  return "success";
};

const getTimelineTipoDetalle = (item) => {
  const raw = String(item?.tipo || "");
  const [, specific] = raw.split(":");
  return specific || item?.titulo || "-";
};

const PostventaBadge = ({ value, tone = "default" }) => (
  <span className={`crm-postventa-badge tone-${tone}`}>{value || "-"}</span>
);

const DataList = ({ items = [] }) => (
  <dl className="crm-postventa-ficha-list">
    {items.map((item) => (
      <div className="crm-postventa-ficha-item" key={`${item.label}-${item.value}`}>
        <dt>{item.label}</dt>
        <dd>
          {item.isBadge ? (
            <PostventaBadge value={item.value} tone={item.tone} />
          ) : (
            item.value || "-"
          )}
        </dd>
      </div>
    ))}
  </dl>
);

const buildClienteGroupKey = (cliente = {}) => {
  const cuit = String(cliente.cuit || "").trim();
  const nombre = String(cliente.nombreDeFantasia || cliente.razonSocial || "")
    .trim()
    .toLowerCase();
  return `${cuit}::${nombre}`;
};

const normalizeTerminalOptions = (terminales = []) =>
  terminales
    .filter((item) => item?.clienteId)
    .sort((a, b) => {
      const left = Number(String(a.terminal || "").replace(/\D/g, "")) || Number.MAX_SAFE_INTEGER;
      const right = Number(String(b.terminal || "").replace(/\D/g, "")) || Number.MAX_SAFE_INTEGER;
      return left - right || String(a.comercio || "").localeCompare(String(b.comercio || ""));
    })
    .map((item, index) => ({
      clienteId: item.clienteId,
      label: item.label || `Terminal ${index + 1}`,
      terminal: item.terminal || item.nDeTerminal || "",
      comercio: item.comercio || item.nDeComercio || "",
      estado: item.estado || "",
      legajo: item.legajo || "",
      resumen:
        item.resumen ||
        [item.terminal ? `Terminal ${item.terminal}` : "", item.comercio ? `Comercio ${item.comercio}` : ""]
          .filter(Boolean)
          .join(" | "),
    }));

const mergeTerminalOptions = (...sources) => {
  const merged = new Map();

  sources
    .flat()
    .forEach((item) => {
      if (!item?.clienteId) {
        return;
      }

      const current = merged.get(item.clienteId) || {};
      merged.set(item.clienteId, {
        ...current,
        ...item,
        label: item.label || current.label,
        resumen: item.resumen || current.resumen,
      });
    });

  return normalizeTerminalOptions(Array.from(merged.values()));
};

const buildTerminalesFromFacturacion = (filas = []) =>
  normalizeTerminalOptions(
    Array.from(
      filas.reduce((acc, row) => {
        if (!row?.clienteId) {
          return acc;
        }

        if (!acc.has(row.clienteId)) {
          acc.set(row.clienteId, {
            clienteId: row.clienteId,
            terminal: row.terminal,
            comercio: row.comercio,
            estado: row.estadoAlerta,
            resumen: [row.terminal ? `Terminal ${row.terminal}` : "", row.comercio ? `Comercio ${row.comercio}` : ""]
              .filter(Boolean)
              .join(" | "),
          });
        }

        return acc;
      }, new Map()).values()
    )
  );

const agruparClientesPorGrupo = (clientes = []) =>
  Array.from(
    clientes.reduce((acc, cliente) => {
      const groupKey = buildClienteGroupKey(cliente);
      if (!groupKey || groupKey === "::") {
        return acc;
      }

      if (!acc.has(groupKey)) {
        acc.set(groupKey, []);
      }

      acc.get(groupKey).push(cliente);
      return acc;
    }, new Map()).entries()
  )
    .map(([groupKey, items]) => {
      const representative =
        items.find((item) => String(item.estado || "").toUpperCase() === "ALTA") ||
        items[0];
      const terminales = normalizeTerminalOptions(
        items.map((item) => ({
          clienteId: item.clienteId,
          terminal: item.nDeTerminal,
          comercio: item.nDeComercio,
          estado: item.estado,
          legajo: item.legajo,
        }))
      );
      const comerciosUnicos = new Set(items.map((item) => item.nDeComercio).filter(Boolean)).size;

      return {
        ...representative,
        groupKey,
        terminales,
        cantidadTerminales: terminales.length,
        cantidadComercios: comerciosUnicos,
        resumen: [
          representative.razonSocial || representative.nombreDeFantasia || "",
          representative.cuit ? `CUIT ${representative.cuit}` : "",
          terminales.length ? `${terminales.length} terminal(es)` : "",
          comerciosUnicos ? `${comerciosUnicos} comercio(s)` : "",
        ]
          .filter(Boolean)
          .join(" | "),
      };
    })
    .sort((a, b) => String(a.nombreDeFantasia || a.razonSocial).localeCompare(String(b.nombreDeFantasia || b.razonSocial)));

const buildFichaEditDraft = (campos = []) =>
  campos.reduce((acc, item) => {
    if (item?.key) {
      acc[item.key] = item.value || "";
    }
    return acc;
  }, {});

const FICHA_SECTION_ORDER = [
  "Datos generales",
  "Contacto",
  "Operativo",
  "Bancario",
  "Fiscal / rentas",
];

const FICHA_FIXED_OPTIONS = {
  estado: [
    { value: "ALTA", label: "Alta" },
    { value: "BAJA", label: "Baja" },
    { value: "SERVICIO.TECNICO", label: "Servicio técnico" },
    { value: "DISPONIBLE", label: "Disponible" },
    { value: "EXTRAVIADA", label: "Extraviada" },
  ],
  siNo: [
    { value: "SI", label: "SI" },
    { value: "NO", label: "NO" },
  ],
  tipoCuenta: [
    { value: "CAJA AHORRO", label: "CAJA AHORRO" },
    { value: "CUENTA CORRIENTE", label: "CUENTA CORRIENTE" },
    { value: "BILLETERA VIRTUAL", label: "BILLETERA VIRTUAL" },
  ],
  estadoAfip: [
    { value: "EXENTO", label: "EXENTO" },
    { value: "INHABILITADO", label: "INHABILITADO" },
    { value: "MONOTRIBUTISTA", label: "MONOTRIBUTISTA" },
    { value: "RESPONSABLE INSCRIPTO", label: "RESPONSABLE INSCRIPTO" },
  ],
  exento: [
    { value: "NO", label: "NO" },
    { value: "EXENTO GANANCIA", label: "EXENTO GANANCIA" },
    { value: "EXENTO IVA", label: "EXENTO IVA" },
  ],
  estadoRentas: [
    { value: "ALTA EN PROCESO", label: "ALTA EN PROCESO" },
    { value: "CM", label: "CM" },
    { value: "EXENTO", label: "EXENTO" },
    { value: "INSCRIPTO", label: "INSCRIPTO" },
    { value: "NO INSCRIPTO", label: "NO INSCRIPTO" },
  ],
  altas: [
    { value: "AGREGA", label: "AGREGA" },
    { value: "AGREGA CT", label: "AGREGA CT" },
    { value: "ALTA CCTT", label: "ALTA CCTT" },
    { value: "ALTA NUEVA", label: "ALTA NUEVA" },
    { value: "ALTA SSTT", label: "ALTA SSTT" },
  ],
  bajas: [
    { value: "BAJA", label: "BAJA" },
    { value: "BAJA POR FACTURACION", label: "BAJA POR FACTURACION" },
    { value: "BAJA POR POSNET", label: "BAJA POR POSNET" },
    { value: "BAJA SSTT", label: "BAJA SSTT" },
    { value: "VERIFONE REUTILIZABLE", label: "VERIFONE REUTILIZABLE" },
    { value: "BAJA POR CAMBIO DE TERMINAL", label: "BAJA POR CAMBIO DE TERMINAL" },
  ],
};

const FICHA_SELECT_KEY_TO_SOURCE = {
  estado: "estado",
  nDeComercio: "comercios",
  legajo: "legajos",
  rubro: "rubros",
  rubroSecundario: "rubros",
  altaAhora12: "siNo",
  provincia: "provincias",
  nombreDelAsesor: "asesores",
  altas: "altas",
  baja: "bajas",
  linkDePago: "siNo",
  banco: "bancos",
  tipoDeCuenta: "tipoCuenta",
  estadoAfip: "estadoAfip",
  exento: "exento",
  estadoRentas: "estadoRentas",
  provincia3: "provincias",
};

const FICHA_READONLY_KEYS = new Set(["sasMutual", "estado", "nDeTerminal", "cbuCvu", "letraCbu", "largoCbu", "alicuota"]);

const normalizeOption = (value, label = value, extra = {}) => ({
  value: value == null ? "" : String(value),
  label: label == null ? String(value ?? "") : String(label),
  ...extra,
});

const dedupeOptions = (options = []) => {
  const seen = new Set();

  return options.filter((option) => {
    const key = String(option?.value ?? "");
    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const mapFichaDraftToPayload = (draft = {}) => ({
  SasMutual: draft.sasMutual || "",
  Estado: draft.estado || "",
  NDeTerminal: draft.nDeTerminal || "",
  NDeComercio: draft.nDeComercio || "",
  NroLocal: draft.nroLocal || "",
  Legajo: draft.legajo || "",
  NombreDeFantasia: draft.nombreDeFantasia || "",
  Domicilio: draft.domicilio || "",
  Localidad: draft.localidad || "",
  CodigoPostal: draft.codigoPostal || "",
  Telefono: draft.telefono || "",
  LinkDePago: draft.linkDePago || "",
  Rubro: draft.rubro || "",
  RubroSecundario: draft.rubroSecundario || "",
  AltaAhora12: draft.altaAhora12 || "",
  FechaDeAlta: draft.fechaDeAlta || "",
  FechaDeBaja: draft.fechaDeBaja || "",
  TipoDeBaja: draft.tipoDeBaja || "",
  Provincia: draft.provincia || "",
  NombreDelAsesor: draft.nombreDelAsesor || "",
  Altas: draft.altas || "",
  Baja: draft.baja || "",
  Mail: draft.mail || "",
  Titular: draft.titular || "",
  CuitCuil: draft.cuitCuil || "",
  Banco: draft.banco || "",
  TipoDeCuenta: draft.tipoDeCuenta || "",
  CbuCvu: draft.cbuCvu || "",
  NDeCuenta: draft.nDeCuenta || "",
  LetraCbu: draft.letraCbu || "",
  LargoCbu: draft.largoCbu || "",
  Alias: draft.alias || "",
  RazonSocial: draft.razonSocial || "",
  CuitRazonSocial: draft.cuitRazonSocial || "",
  EstadoAfip: draft.estadoAfip || "",
  CodActividad: draft.codActividad || "",
  Actividad: draft.actividad || "",
  Exento: draft.exento || "",
  EstadoRentas: draft.estadoRentas || "",
  Actividad2: draft.actividad2 || "",
  DomicilioFiscal: draft.domicilioFiscal || "",
  Provincia3: draft.provincia3 || "",
  Alicuota: draft.alicuota || "",
  CodActividadRentas: draft.codActividadRentas || "",
  ActividadRentas: draft.actividadRentas || "",
  AutorizadoParaCambiosAbm: draft.autorizadoParaCambiosAbm || "",
});

const groupFichaFieldsBySection = (campos = []) =>
  campos.reduce((acc, item) => {
    const section = item?.section || "General";
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(item);
    return acc;
  }, {});

const sortFichaSections = (grouped = {}) =>
  FICHA_SECTION_ORDER.reduce((acc, section) => {
    if (grouped[section]?.length) {
      acc[section] = grouped[section];
    }
    return acc;
  }, {});

const buildFichaCatalogOptions = (catalogos = {}, field) => {
  const source = field?.dataSource || FICHA_SELECT_KEY_TO_SOURCE[field?.key];
  if (!source) {
    return [];
  }

  if (FICHA_FIXED_OPTIONS[source]) {
    return FICHA_FIXED_OPTIONS[source];
  }

  return catalogos[source] || [];
};

const hasLetters = (value = "") => /[a-zA-Z]/.test(String(value));

const applyFichaFieldSideEffects = (prevDraft, fieldKey, nextValue, catalogos = {}) => {
  const nextDraft = {
    ...prevDraft,
    [fieldKey]: nextValue,
  };

  if (fieldKey === "tipoDeCuenta") {
    nextDraft.cbuCvu = nextValue === "BILLETERA VIRTUAL" ? "CVU" : nextValue ? "CBU" : "";
  }

  if (fieldKey === "nDeCuenta") {
    const rawValue = String(nextValue || "");
    nextDraft.letraCbu = rawValue ? (hasLetters(rawValue) ? "CORRECTO" : "INCORRECTO") : "";
    nextDraft.largoCbu = rawValue ? String(rawValue.length) : "";
  }

  if (fieldKey === "provincia3") {
    const provincias = catalogos.provincias || [];
    const selected = provincias.find((item) => item.value === nextValue);
    nextDraft.alicuota = selected?.alicuota || "";
  }

  return nextDraft;
};

async function fetchFichaCatalogos() {
  const [bancosRes, rubrosRes, provinciasRes, legajosRes, comerciosRes, asesoresRes] = await Promise.all([
    fetch("/api/Banco/lista"),
    fetch("/api/Rubro/lista"),
    fetch("/api/Provincia/listaprovincia"),
    fetch("/api/Legajo/lista"),
    fetch("/api/Comercio/lista"),
    fetch("/api/Usuarios/obtenergestoresyasesores"),
  ]);

  const responses = [bancosRes, rubrosRes, provinciasRes, legajosRes, comerciosRes, asesoresRes];
  const failed = responses.find((response) => !response.ok);
  if (failed) {
    throw new Error("No se pudieron cargar los catálogos de edición.");
  }

  const [bancosData, rubrosData, provinciasData, legajosData, comerciosData, asesoresData] = await Promise.all(
    responses.map((response) => response.json())
  );

  return {
    bancos: (Array.isArray(bancosData) ? bancosData : []).map((item) =>
      normalizeOption(item?.nomBanco ?? item?.NomBanco ?? "", item?.nomBanco ?? item?.NomBanco ?? "")
    ),
    rubros: (Array.isArray(rubrosData) ? rubrosData : []).map((item) => normalizeOption(item, item)),
    provincias: (Array.isArray(provinciasData) ? provinciasData : []).map((item) =>
      normalizeOption(item?.provincia ?? item?.Provincia ?? "", item?.provincia ?? item?.Provincia ?? "", {
        alicuota: item?.alicuota ?? item?.Alicuota ?? "",
      })
    ),
    legajos: (Array.isArray(legajosData) ? legajosData : []).map((item) =>
      normalizeOption(item?.nroLegajo ?? item?.NroLegajo ?? "", item?.nroLegajo ?? item?.NroLegajo ?? "")
    ),
    comercios: (Array.isArray(comerciosData) ? comerciosData : []).map((item) =>
      normalizeOption(item?.num ?? item?.Num ?? "", item?.numComercio ?? item?.NumComercio ?? item?.num ?? item?.Num ?? "")
    ),
    asesores: (Array.isArray(asesoresData) ? asesoresData : []).map((item) => normalizeOption(item, item)),
  };
}

async function fetchFichaCatalogosV2() {
  const fetchJsonWithFallback = async (urls = []) => {
    let lastError = null;

    for (const url of urls) {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          lastError = new Error(`Error cargando ${url}: ${response.status}`);
          continue;
        }

        return response.json();
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError || new Error("No se pudieron cargar los catalogos de edicion.");
  };

  const [bancosData, rubrosData, provinciasData, legajosData, comerciosData, asesoresData] = await Promise.all([
    fetchJsonWithFallback(["/api/Banco/lista"]),
    fetchJsonWithFallback(["/api/Rubro/lista"]),
    fetchJsonWithFallback(["/api/Provincia/listaprovincia"]),
    fetchJsonWithFallback(["/api/Legajo/lista", "/api/Legajo/listacompleto"]),
    fetchJsonWithFallback(["/api/Comercio/lista"]),
    fetchJsonWithFallback(["/api/Usuarios/obtenergestoresyasesores"]),
  ]);

  return {
    bancos: dedupeOptions((Array.isArray(bancosData) ? bancosData : []).map((item) =>
      normalizeOption(item?.nomBanco ?? item?.NomBanco ?? "", item?.nomBanco ?? item?.NomBanco ?? "")
    )),
    rubros: dedupeOptions((Array.isArray(rubrosData) ? rubrosData : []).map((item) =>
      normalizeOption(item?.nomRubro ?? item?.NomRubro ?? item, item?.nomRubro ?? item?.NomRubro ?? item)
    )),
    provincias: dedupeOptions((Array.isArray(provinciasData) ? provinciasData : []).map((item) =>
      normalizeOption(item?.provincia ?? item?.Provincia ?? "", item?.provincia ?? item?.Provincia ?? "", {
        alicuota: item?.alicuota ?? item?.Alicuota ?? "",
      })
    )),
    legajos: dedupeOptions((Array.isArray(legajosData) ? legajosData : []).map((item) => {
      const legajoValue = item?.nroLegajo ?? item?.NroLegajo ?? "";
      const nombreFantasia = item?.nombreDeFantasia ?? item?.NombreDeFantasia ?? "";
      const label = nombreFantasia ? `${legajoValue} - ${nombreFantasia}` : legajoValue;
      return normalizeOption(legajoValue, label);
    })),
    comercios: dedupeOptions((Array.isArray(comerciosData) ? comerciosData : []).map((item) =>
      normalizeOption(
        item?.num ?? item?.Num ?? item?.numComercio ?? item?.NumComercio ?? "",
        item?.numComercio ?? item?.NumComercio ?? item?.num ?? item?.Num ?? ""
      )
    )),
    asesores: dedupeOptions((Array.isArray(asesoresData) ? asesoresData : []).map((item) =>
      normalizeOption(item?.nombre ?? item?.Nombre ?? item, item?.nombre ?? item?.Nombre ?? item)
    )),
  };
}

const resolveMotivoCodigoFromRegla = (reglas = [], reglaId) => {
  const regla = reglas.find((item) => Number(item?.id) === Number(reglaId));
  return regla?.nombreAccion || "";
};

const resolveMotivoCodigoFromAlerta = (alerta = {}) => {
  const motivoCodigo = String(alerta?.motivoCodigo || "").trim();
  if (motivoCodigo) return motivoCodigo;
  const tipo = String(alerta?.tipoAlerta || "").toLowerCase();
  if (tipo.includes("roll")) return "FALTA_ROLLOS";
  if (tipo.includes("contacto vencido")) return "PROXIMO_CONTACTO_VENCIDO";
  if (tipo.includes("caida")) return "CAIDA_FACTURACION";
  if (tipo.includes("factur") || tipo.includes("caída") || tipo.includes("caida") || tipo.includes("actividad")) {
    return "FALTA_FACTURACION";
  }
  return "";
};

const filterManualRules = (reglas = []) =>
  (Array.isArray(reglas) ? reglas : []).filter((regla) =>
    String(regla?.nombreAccion || "").toUpperCase().startsWith("MAN_")
  );

const normalizeWhatsappPhone = (value) => {
  const digits = String(value || "").replace(/\D/g, "").replace(/^0+/, "");
  if (!digits) return "";
  return digits.startsWith("54") ? digits : `54${digits}`;
};

const buildSuggestedObservation = (item, estadoGestion) => {
  const lines = [
    `Motivo: ${item?.tipoAlerta || item?.motivo || "-"}`,
    `Accion sugerida: ${item?.accionSugerida || "-"}`,
    item?.mensajeSugerido ? `Mensaje sugerido: ${item.mensajeSugerido}` : "",
    item?.resumen ? `Contexto: ${item.resumen}` : "",
    estadoGestion ? `Resultado: ${estadoGestion}` : "",
  ].filter(Boolean);

  return lines.join("\n");
};

const CrmPostventa = () => {
  const { darkMode } = useContext(DarkModeContext);
  const location = useLocation();
  const navigate = useNavigate();
  const apiUrlToken = process.env.REACT_APP_API_TOKEN;

  const [cargandoSesion, setCargandoSesion] = useState(true);
  const [clienteModalOpen, setClienteModalOpen] = useState(false);
  const [clienteSearch, setClienteSearch] = useState("");
  const [clienteResults, setClienteResults] = useState([]);
  const [clienteLoading, setClienteLoading] = useState(false);
  const [clienteSeleccionadoId, setClienteSeleccionadoId] = useState(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? Number(raw) : null;
  });
  const [clientePendiente, setClientePendiente] = useState(null);
  const [terminalesGrupo, setTerminalesGrupo] = useState([]);
  const [ficha, setFicha] = useState(null);
  const [fichaEditOpen, setFichaEditOpen] = useState(false);
  const [fichaEditDraft, setFichaEditDraft] = useState({});
  const [fichaCatalogos, setFichaCatalogos] = useState({
    bancos: [],
    rubros: [],
    provincias: [],
    legajos: [],
    comercios: [],
    asesores: [],
  });
  const [fichaCatalogosLoading, setFichaCatalogosLoading] = useState(false);
  const [facturacion, setFacturacion] = useState(null);
  const [alertas, setAlertas] = useState(null);
  const [gestiones, setGestiones] = useState(null);
  const [seguimiento, setSeguimiento] = useState(null);
  const [ordenesPago, setOrdenesPago] = useState(null);
  const [cupones, setCupones] = useState(null);
  const [rolloModalOpen, setRolloModalOpen] = useState(false);
  const [errores, setErrores] = useState({});
  const [cargas, setCargas] = useState({
    ficha: false,
    facturacion: false,
    alertas: false,
    ordenesPago: false,
    cupones: false,
    gestiones: false,
    seguimiento: false,
    guardandoFicha: false,
    guardandoGestion: false,
    guardandoAlertaGestion: false,
    guardandoRollo: false,
  });
  const [analyticsRange, setAnalyticsRange] = useState({
    desde: makeMonthStart(),
    hasta: makeToday(),
  });
  const [gestionDraft, setGestionDraft] = useState({
    reglaId: "",
    estadoGestion: "Pendiente",
    observacion: "",
    fechaGestion: `${makeToday()}T09:00`,
    fechaProximoContacto: "",
  });
  const [rolloDraft, setRolloDraft] = useState({
    fechaEntrega: makeToday(),
    cantidad: "",
    observacion: "",
  });
  const [alertasFiltros, setAlertasFiltros] = useState({
    fecha: makeToday(),
    estado: "",
    prioridad: "",
    tipo: "",
    provincia: "",
    responsable: "",
    search: "",
  });
  const [alertasSearchDraft, setAlertasSearchDraft] = useState("");
  const [alertasOperativasSearch, setAlertasOperativasSearch] = useState("");
  const [ordenesPagoFiltros, setOrdenesPagoFiltros] = useState({
    fecha: makeToday(),
    search: "",
  });
  const [cuponesFiltros, setCuponesFiltros] = useState({
    mes: makeCurrentMonth(),
    search: "",
  });
  const [facturacionFiltroComercio, setFacturacionFiltroComercio] = useState("");
  const [facturacionDetalle, setFacturacionDetalle] = useState(null);
  const [facturacionAccionItem, setFacturacionAccionItem] = useState(null);
  const [facturacionGestionItem, setFacturacionGestionItem] = useState(null);
  const [facturacionGestionDraft, setFacturacionGestionDraft] = useState({
    clienteId: 0,
    reglaId: "",
    estadoGestion: "Pendiente",
    observacion: "",
    fechaGestion: `${makeToday()}T09:00`,
    fechaProximoContacto: "",
  });
  const [timelineDetalle, setTimelineDetalle] = useState(null);
  const [alertaGestionDraft, setAlertaGestionDraft] = useState({
    clienteId: 0,
    reglaId: "",
    estadoGestion: "Pendiente",
    observacion: "",
    fechaGestion: `${makeToday()}T09:00`,
    fechaProximoContacto: "",
  });
  const [alertaSeleccionada, setAlertaSeleccionada] = useState(null);
  const [alertaAccionItem, setAlertaAccionItem] = useState(null);

  const section = useMemo(() => {
    if (location.pathname.includes("/facturacion")) return "facturacion";
    if (location.pathname.includes("/alertas")) return "alertas";
    if (location.pathname.includes("/ordenes-pago")) return "ordenes-pago";
    if (location.pathname.includes("/cupones")) return "cupones";
    if (location.pathname.includes("/ficha")) return "ficha";
    if (location.pathname.includes("/herramientas")) return "herramientas";
    if (location.pathname.includes("/gestion")) return "gestion";
    if (location.pathname.includes("/seguimiento")) return "seguimiento";
    return "alertas";
  }, [location.pathname]);

  const clienteGrupos = useMemo(() => agruparClientesPorGrupo(clienteResults), [clienteResults]);
  const currentGroupKey = useMemo(
    () => buildClienteGroupKey(clientePendiente || ficha?.header || {}),
    [clientePendiente, ficha?.header]
  );
  const clienteSeleccionadoData = useMemo(() => clientePendiente || ficha?.header || {}, [clientePendiente, ficha?.header]);
  const clienteSeleccionadoCuit = useMemo(
    () => normalizeDigits(clienteSeleccionadoData?.cuit || clienteSeleccionadoData?.Cuit),
    [clienteSeleccionadoData]
  );
  const clienteSeleccionadoComercio = useMemo(
    () => normalizeDigits(clienteSeleccionadoData?.nDeComercio || clienteSeleccionadoData?.NDeComercio || clienteSeleccionadoData?.comercio),
    [clienteSeleccionadoData]
  );
  const clienteSeleccionadoTerminal = useMemo(
    () => normalizeDigits(clienteSeleccionadoData?.nDeTerminal || clienteSeleccionadoData?.NDeTerminal || clienteSeleccionadoData?.terminal),
    [clienteSeleccionadoData]
  );
  const cuponesTerminalesCliente = useMemo(
    () =>
      Array.from(
        new Set(
          [
            clienteSeleccionadoTerminal,
            ...terminalesGrupo.map((item) => normalizeDigits(item?.terminal)),
          ].filter(Boolean)
        )
      ),
    [clienteSeleccionadoTerminal, terminalesGrupo]
  );
  const cuponesComerciosCliente = useMemo(
    () =>
      Array.from(
        new Set(
          [
            clienteSeleccionadoComercio,
            ...terminalesGrupo.map((item) => normalizeDigits(item?.comercio)),
          ].filter(Boolean)
        )
      ),
    [clienteSeleccionadoComercio, terminalesGrupo]
  );
  const cuponesNombresCliente = useMemo(
    () =>
      Array.from(
        new Set(
          [
            clienteSeleccionadoData?.nombreDeFantasia,
            clienteSeleccionadoData?.razonSocial,
            clienteSeleccionadoData?.nombreComercio,
            ficha?.header?.nombreDeFantasia,
            ficha?.header?.razonSocial,
            ficha?.header?.nombreComercio,
          ]
            .map((item) => normalizeText(item))
            .filter(Boolean)
        )
      ),
    [clienteSeleccionadoData, ficha?.header]
  );
  const manualAlertaRules = useMemo(() => filterManualRules(alertas?.reglas || []), [alertas?.reglas]);
  const manualGestionRules = useMemo(() => filterManualRules(gestiones?.reglas || []), [gestiones?.reglas]);

  const clearSession = useCallback(() => {
    sessionStorage.removeItem("token");
    navigate("/");
    window.location.reload();
  }, [navigate]);

  const copiarMensajeSugerido = useCallback(async (item, title = "CRM Postventa") => {
    const mensaje = String(item?.mensajeSugerido || "").trim();
    if (!mensaje) {
      Swal.fire(title, "No hay mensaje sugerido para copiar.", "info");
      return;
    }

    try {
      await navigator.clipboard.writeText(mensaje);
      Swal.fire({
        icon: "success",
        title: "Mensaje copiado",
        text: "El mensaje sugerido quedo en el portapapeles.",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire(title, "No se pudo copiar el mensaje sugerido.", "error");
    }
  }, []);

  const abrirWhatsappSugerido = useCallback((item, title = "CRM Postventa") => {
    const mensaje = String(item?.mensajeSugerido || "").trim();
    const telefono = normalizeWhatsappPhone(item?.telefono);

    if (!telefono) {
      Swal.fire(title, "El cliente no tiene telefono cargado.", "info");
      return;
    }

    const url = `https://wa.me/${telefono}?text=${encodeURIComponent(mensaje)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  const descargarOrdenPago = useCallback(async (item) => {
    if (!item?.documentoId || !item?.cuit || !item?.fechaSubida) {
      Swal.fire("Órdenes de pago", "La orden seleccionada no tiene los datos necesarios para descargar el PDF.", "info");
      return;
    }

    try {
      await descargarOrdenPagoLegacy({
        documentoId: item.documentoId,
        fechaPago: item.fechaSubida,
        nombreComercio: item.nombreDocumento,
        cuit: item.cuit,
      });
    } catch (error) {
      console.error("Error descargando orden de pago:", error);
      Swal.fire("Órdenes de pago", error.message || "No se pudo descargar el PDF.", "error");
    }
  }, []);

  const mostrarAccesoDenegado = useCallback(
    (title, text) => {
      Swal.fire({
        title,
        text,
        imageUrl: logo,
        imageWidth: 100,
        imageHeight: 30,
        imageAlt: "Logo",
        confirmButtonText: "Ok",
        timer: 5000,
        allowOutsideClick: true,
        customClass: {
          container: darkMode ? "swal2-container--dark" : "swal2-container--light",
          confirmButton: "my-swal-button",
        },
      }).then(() => clearSession());
    },
    [clearSession, darkMode]
  );

  const validarSesion = useCallback(async () => {
    const token = getToken();
    if (!token) {
      mostrarAccesoDenegado("Sesión expirada o token inválido.", "Inicie sesión nuevamente.");
      return;
    }

    try {
      const response = await fetch(apiUrlToken, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ Token: token }),
      });

      if (response.status === 401 || response.status === 403) {
        mostrarAccesoDenegado("Sesión expirada o token inválido.", "Inicie sesión nuevamente.");
        return;
      }

      if (!response.ok) {
        throw new Error(`Error ${response.status}`);
      }

      const raw = await response.text();
      let rol = Number(raw);
      if (!Number.isFinite(rol)) {
        rol = Number(JSON.parse(raw));
      }

      if (rol !== 8) {
        Swal.fire({
          title: "Acceso denegado.",
          text: "No tiene permisos para este panel.",
          imageUrl: logo,
          imageWidth: 100,
          imageHeight: 30,
          imageAlt: "Logo",
          confirmButtonText: "Ok",
          timer: 5000,
          allowOutsideClick: true,
          customClass: {
            container: darkMode ? "swal2-container--dark" : "swal2-container--light",
            confirmButton: "my-swal-button",
          },
        }).then(() => navigate("/"));
        return;
      }
    } catch (error) {
      console.error("Error validando sesión CRM Postventa:", error);
      Swal.fire("Error", "No se pudo validar la sesión en este momento.", "error");
      return;
    }

    setCargandoSesion(false);
  }, [apiUrlToken, darkMode, mostrarAccesoDenegado, navigate]);

  const cargarClientes = useCallback(async (searchValue = "") => {
    setClienteLoading(true);
    try {
      const data = await crmPostventaApi.buscarClientes(searchValue);
      setClienteResults(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error buscando clientes:", error);
      setClienteResults([]);
      Swal.fire("Error", error.message || "No se pudieron buscar clientes.", "error");
    } finally {
      setClienteLoading(false);
    }
  }, []);

  const cargarAlertas = useCallback(async (filtrosActuales) => {
    setCargas((prev) => ({ ...prev, alertas: true }));
    try {
      let generacionAutomatica = null;
      try {
        generacionAutomatica = await crmPostventaApi.generarGestionesAutomaticas(filtrosActuales?.fecha || makeToday());
      } catch (errorGeneracion) {
        console.error("Error generando gestiones automáticas:", errorGeneracion);
      }

      const data = await crmPostventaApi.obtenerAlertas(filtrosActuales);
      setAlertas({
        ...data,
        generacionAutomatica,
      });
      setErrores((prev) => ({ ...prev, alertas: undefined }));
    } catch (error) {
      console.error("Error cargando alertas:", error);
      setAlertas(null);
      setErrores((prev) => ({ ...prev, alertas: error.message || "No se pudieron cargar las alertas." }));
    } finally {
      setCargas((prev) => ({ ...prev, alertas: false }));
    }
  }, []);

  const cargarOrdenesPago = useCallback(async (clienteId, filtrosActuales) => {
    if (!clienteId) {
      return;
    }

    setCargas((prev) => ({ ...prev, ordenesPago: true }));
    try {
      const fechaConsultada = filtrosActuales?.fecha || makeToday();
      const search = String(filtrosActuales?.search || clienteSeleccionadoCuit || "").trim();
      const legacyRows = await crmPostventaApi.obtenerOrdenesPagoLegacy(fechaConsultada);
      const rows = Array.isArray(legacyRows)
        ? legacyRows.filter((item) =>
            !search ||
            String(item?.cuit || "").includes(search) ||
            String(item?.nombreDocumento || "").toLowerCase().includes(search.toLowerCase())
          )
        : [];

      setOrdenesPago({
        tieneDatosReales: rows.length > 0,
        message: rows.length > 0 ? "" : "No se encontraron órdenes de pago para la fecha y CUIT seleccionados.",
        fechaPago: formatLegacyDisplayDate(fechaConsultada),
        cuitFiltrado: search,
        filas: rows,
      });
      setErrores((prev) => ({ ...prev, ordenesPago: undefined }));
    } catch (error) {
      console.error("Error cargando ordenes de pago:", error);
      setOrdenesPago(null);
      setErrores((prev) => ({ ...prev, ordenesPago: error.message || "No se pudieron cargar las ordenes de pago." }));
    } finally {
      setCargas((prev) => ({ ...prev, ordenesPago: false }));
    }
  }, [clienteSeleccionadoCuit]);

  const cargarCupones = useCallback(async (clienteId, filtrosActuales) => {
    if (!clienteId) {
      return;
    }

    setCargas((prev) => ({ ...prev, cupones: true }));
    try {
      const mesConsultado = filtrosActuales?.mes || makeCurrentMonth();
      const search = String(filtrosActuales?.search || "").trim().toLowerCase();
      const legacyRows = await crmPostventaApi.obtenerCuponesLegacy(`${mesConsultado}-01`);
      const allRows = Array.from(
        (Array.isArray(legacyRows) ? legacyRows : []).reduce((acc, item) => {
          const normalized = normalizeCuponLegacyRow(item);
          acc.set(buildCuponDedupeKey(normalized), normalized);
          return acc;
        }, new Map()).values()
      );
      const rowsByTerminal = cuponesTerminalesCliente.length
        ? allRows.filter((item) => cuponesTerminalesCliente.includes(normalizeDigits(item?.nroDeComercio)))
        : [];
      const rowsByComercio = cuponesComerciosCliente.length
        ? allRows.filter((item) => cuponesComerciosCliente.includes(normalizeDigits(item?.nroDeComercio)))
        : [];
      const rowsByNombre = cuponesNombresCliente.length
        ? allRows.filter((item) => {
            const nombreRow = normalizeText(item?.nombreComercio);
            return cuponesNombresCliente.some((nombre) => nombreRow.includes(nombre) || nombre.includes(nombreRow));
          })
        : [];
      const rowsByCuit = clienteSeleccionadoCuit
        ? allRows.filter((item) => normalizeDigits(item?.cuit) === clienteSeleccionadoCuit)
        : [];

      let rows = allRows;
      if (rowsByTerminal.length) {
        rows = rowsByTerminal;
      } else if (rowsByComercio.length) {
        rows = rowsByComercio;
      } else if (rowsByNombre.length) {
        rows = rowsByNombre;
      } else if (rowsByCuit.length) {
        rows = rowsByCuit;
      } else {
        rows = [];
      }

      rows = rows
        .filter((item) => {
          if (!search) {
            return true;
          }

          const haystack = [
            item?.fechaOperacion,
            item?.fechaDePago,
            item?.nroDeCupon,
            item?.nroDeComercio,
            item?.nroDeTarjeta,
            item?.brutoRaw,
            item?.totalBruto,
            item?.tarjeta,
            item?.provincia,
            item?.nombreComercio,
            item?.cuit,
          ]
            .join(" ")
            .toLowerCase();

          return haystack.includes(search);
        });

      const resumen = buildCuponSummary(rows);

      setCupones({
        tieneDatosReales: rows.length > 0,
        message: rows.length > 0 ? "" : "No se encontraron cupones para el cliente seleccionado.",
        mesConsultado: formatLegacyMonthLabel(mesConsultado),
        criterioFiltrado:
          cuponesTerminalesCliente.join(", ") ||
          cuponesComerciosCliente.join(", ") ||
          cuponesNombresCliente.join(", ") ||
          clienteSeleccionadoCuit ||
          "-",
        resumen,
        filas: rows,
      });
      setErrores((prev) => ({ ...prev, cupones: undefined }));
    } catch (error) {
      console.error("Error cargando cupones:", error);
      setCupones(null);
      setErrores((prev) => ({ ...prev, cupones: error.message || "No se pudieron cargar los cupones." }));
    } finally {
      setCargas((prev) => ({ ...prev, cupones: false }));
    }
  }, [clienteSeleccionadoCuit, cuponesComerciosCliente, cuponesNombresCliente, cuponesTerminalesCliente]);

  const cargarModulo = useCallback(async (clienteId, range) => {
    if (!clienteId) {
      return;
    }

    setCargas((prev) => ({
      ...prev,
      ficha: true,
      facturacion: true,
      gestiones: true,
      seguimiento: true,
      guardandoGestion: false,
    }));
    setErrores({});

    const results = await Promise.allSettled([
      crmPostventaApi.obtenerFicha(clienteId),
      crmPostventaApi.obtenerFacturacion(clienteId, range.desde, range.hasta),
      crmPostventaApi.obtenerGestiones(clienteId),
      crmPostventaApi.obtenerSeguimiento(clienteId, range.desde, range.hasta),
    ]);

    const [fichaRes, facturacionRes, gestionesRes, seguimientoRes] = results;

    if (fichaRes.status === "fulfilled") {
      setFicha(fichaRes.value);
    } else {
      setFicha(null);
      setErrores((prev) => ({ ...prev, ficha: fichaRes.reason?.message || "No se pudo cargar la ficha." }));
    }

    if (facturacionRes.status === "fulfilled") {
      setFacturacion(facturacionRes.value);
    } else {
      setFacturacion(null);
      setErrores((prev) => ({ ...prev, facturacion: facturacionRes.reason?.message || "No se pudo cargar facturación." }));
    }

    if (gestionesRes.status === "fulfilled") {
      let gestionesValue = gestionesRes.value;
      const clienteKey =
        gestionesRes.value?.rollos?.clienteKey ||
        fichaRes.value?.header?.clienteKey ||
        crmPostventaApi.buildClienteKey(fichaRes.value?.header || {});

      if (clienteKey) {
        try {
          const rollos = await crmPostventaApi.obtenerRollos(clienteKey, clienteId);
          gestionesValue = {
            ...gestionesValue,
            rollos,
          };
        } catch (error) {
          console.error("Error cargando rollos:", error);
        }
      }

      setGestiones(gestionesValue);
      setGestionDraft((prev) => ({
        ...prev,
        reglaId: filterManualRules(gestionesValue?.reglas || [])?.[0]?.id ? String(filterManualRules(gestionesValue?.reglas || [])[0].id) : "",
      }));
    } else {
      setGestiones(null);
      setErrores((prev) => ({ ...prev, gestiones: gestionesRes.reason?.message || "No se pudieron cargar las gestiones." }));
    }

    if (seguimientoRes.status === "fulfilled") {
      setSeguimiento(seguimientoRes.value);
    } else {
      setSeguimiento(null);
      setErrores((prev) => ({ ...prev, seguimiento: seguimientoRes.reason?.message || "No se pudo cargar el seguimiento." }));
    }

    const terminalesDesdeFacturacion =
      facturacionRes.status === "fulfilled"
        ? buildTerminalesFromFacturacion(facturacionRes.value?.filas || [])
        : [];
    const terminalesDesdeFicha =
      fichaRes.status === "fulfilled"
        ? normalizeTerminalOptions(fichaRes.value?.header?.terminalesGrupo || [])
        : [];

    setTerminalesGrupo(mergeTerminalOptions(terminalesDesdeFicha, terminalesDesdeFacturacion));

    setCargas((prev) => ({
      ...prev,
      ficha: false,
      facturacion: false,
      gestiones: false,
      seguimiento: false,
    }));
  }, []);

  const seleccionarCliente = useCallback(
    (cliente) => {
      if (!cliente?.clienteId) {
        return;
      }

      localStorage.setItem(STORAGE_KEY, String(cliente.clienteId));
      setClienteSeleccionadoId(cliente.clienteId);
      setClientePendiente(cliente);
      const terminales = normalizeTerminalOptions(cliente.terminales || []);
      if (terminales.length) {
        setTerminalesGrupo(terminales);
      }
      setClienteModalOpen(false);
      setFacturacionFiltroComercio("");
      setFacturacionDetalle(null);
      setTimelineDetalle(null);
      setOrdenesPago(null);
      setCupones(null);
      navigate(SECTION_PATHS[section] || SECTION_PATHS.alertas);
    },
    [navigate, section]
  );

  useEffect(() => {
    validarSesion();
  }, [validarSesion]);

  useEffect(() => {
    let isMounted = true;

    const loadFichaCatalogos = async () => {
      setFichaCatalogosLoading(true);
      try {
        const data = await fetchFichaCatalogosV2();
        if (isMounted) {
          setFichaCatalogos(data);
        }
      } catch (error) {
        console.error("Error cargando catálogos de edición:", error);
      } finally {
        if (isMounted) {
          setFichaCatalogosLoading(false);
        }
      }
    };

    loadFichaCatalogos();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!clienteModalOpen) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      cargarClientes(clienteSearch);
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [clienteModalOpen, clienteSearch, cargarClientes]);

  useEffect(() => {
    if (!clienteModalOpen || clientePendiente?.groupKey || !clienteGrupos.length || !currentGroupKey) {
      return;
    }

    const grupoActual = clienteGrupos.find((item) => item.groupKey === currentGroupKey);
    if (grupoActual) {
      setClientePendiente(grupoActual);
    }
  }, [clienteGrupos, clienteModalOpen, clientePendiente?.groupKey, currentGroupKey]);

  useEffect(() => {
    if (!cargandoSesion && clienteSeleccionadoId) {
      cargarModulo(clienteSeleccionadoId, analyticsRange);
    }
  }, [analyticsRange, cargarModulo, cargandoSesion, clienteSeleccionadoId]);

  useEffect(() => {
    if (!clienteSeleccionadoCuit) {
      return;
    }

    setOrdenesPagoFiltros((prev) =>
      prev.search === clienteSeleccionadoCuit
        ? prev
        : {
            ...prev,
            search: clienteSeleccionadoCuit,
          }
    );
  }, [clienteSeleccionadoCuit, clienteSeleccionadoId]);

  useEffect(() => {
    if (cargandoSesion || section !== "alertas") {
      return;
    }

    cargarAlertas(alertasFiltros);
  }, [
    alertasFiltros.estado,
    alertasFiltros.fecha,
    alertasFiltros.prioridad,
    alertasFiltros.provincia,
    alertasFiltros.responsable,
    alertasFiltros.tipo,
    cargarAlertas,
    cargandoSesion,
    section,
  ]);

  useEffect(() => {
    if (cargandoSesion || section !== "ordenes-pago" || !clienteSeleccionadoId) {
      return;
    }

    cargarOrdenesPago(clienteSeleccionadoId, ordenesPagoFiltros);
  }, [cargandoSesion, cargarOrdenesPago, clienteSeleccionadoId, section]);

  useEffect(() => {
    if (cargandoSesion || section !== "cupones" || !clienteSeleccionadoId) {
      return;
    }

    cargarCupones(clienteSeleccionadoId, cuponesFiltros);
  }, [cargandoSesion, cargarCupones, clienteSeleccionadoId, section]);

  const clienteHeader = ficha?.header || {
    clienteKey: clientePendiente?.clienteKey || "",
    nombreDeFantasia: clientePendiente?.nombreDeFantasia || "Sin cliente seleccionado",
    razonSocial: clientePendiente?.razonSocial || "",
    cuit: clientePendiente?.cuit || "",
    estado: clientePendiente?.estado || "",
    producto: clientePendiente?.producto || "",
    nDeTerminal: clientePendiente?.nDeTerminal || "",
    nDeComercio: clientePendiente?.nDeComercio || "",
    legajo: clientePendiente?.legajo || "",
  };

  const handleCambiarTerminal = useCallback((terminal) => {
    if (!terminal?.clienteId || terminal.clienteId === clienteSeleccionadoId) {
      return;
    }

    localStorage.setItem(STORAGE_KEY, String(terminal.clienteId));
    setClienteSeleccionadoId(terminal.clienteId);
    setFacturacionDetalle(null);
    setTimelineDetalle(null);
    setOrdenesPago(null);
    setCupones(null);
  }, [clienteSeleccionadoId]);

  const filasFacturacion = useMemo(() => {
    const allRows = Array.isArray(facturacion?.filas) ? facturacion.filas : [];
    if (!facturacionFiltroComercio) {
      return allRows;
    }

    return allRows.filter(
      (row) =>
        row.terminal === facturacionFiltroComercio ||
        row.comercio === facturacionFiltroComercio
    );
  }, [facturacion?.filas, facturacionFiltroComercio]);

  const alertasOperativasFiltradas = useMemo(() => {
    const filas = Array.isArray(alertas?.filas) ? alertas.filas : [];
    const needle = String(alertasOperativasSearch || "").trim().toLowerCase();

    if (!needle) {
      return filas;
    }

    return filas.filter((item) =>
      [
        item?.comercio,
        item?.cuit,
        item?.terminal,
        item?.provincia,
        item?.tipoAlerta,
        item?.responsable,
        item?.estado,
        item?.resumen,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle)
      );
  }, [alertas?.filas, alertasOperativasSearch]);

  const fichaCamposEdicion = useMemo(
    () => Array.isArray(ficha?.edicion?.campos) ? ficha.edicion.campos : [],
    [ficha?.edicion?.campos]
  );

  const fichaCamposAgrupados = useMemo(
    () => sortFichaSections(groupFichaFieldsBySection(fichaCamposEdicion)),
    [fichaCamposEdicion]
  );

  const abrirEdicionFicha = useCallback(() => {
    setFichaEditDraft(buildFichaEditDraft(fichaCamposEdicion));
    setFichaEditOpen(true);
  }, [fichaCamposEdicion]);

  const guardarEdicionFicha = async (event) => {
    event.preventDefault();
    if (!clienteSeleccionadoId) {
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoFicha: true }));
    try {
      const resultado = await crmPostventaApi.actualizarFicha(
        clienteSeleccionadoId,
        mapFichaDraftToPayload(fichaEditDraft)
      );

      setFichaEditOpen(false);
      await cargarModulo(clienteSeleccionadoId, analyticsRange);

      const warningText = Array.isArray(resultado?.warnings) && resultado.warnings.length
        ? `\n\n${resultado.warnings.join("\n")}`
        : "";

      Swal.fire("Ficha", `${resultado?.message || "Datos actualizados correctamente."}${warningText}`, "success");
    } catch (error) {
      console.error("Error guardando ficha:", error);
      Swal.fire("Error", error.message || "No se pudieron actualizar los datos del cliente.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoFicha: false }));
    }
  };

  const handleFichaFieldChange = useCallback((field, nextValue) => {
    setFichaEditDraft((prev) => applyFichaFieldSideEffects(prev, field.key, nextValue, fichaCatalogos));
  }, [fichaCatalogos]);

  const renderFichaEditField = useCallback((field) => {
    const value = fichaEditDraft[field.key] ?? "";
    const baseOptions = buildFichaCatalogOptions(fichaCatalogos, field);
    const options = value && !baseOptions.some((option) => option.value === value)
      ? [...baseOptions, normalizeOption(value, value)]
      : baseOptions;
    const isReadOnly = !field.editable || FICHA_READONLY_KEYS.has(field.key);
    const source = field.dataSource || FICHA_SELECT_KEY_TO_SOURCE[field.key];

    if (field.inputType === "select") {
      return (
        <div className={`crm-postventa-select-control ${isReadOnly ? "is-readonly" : ""}`}>
          <Form.Select
            className={`crm-postventa-input ${isReadOnly ? "is-readonly" : ""}`}
            value={value}
            disabled={isReadOnly}
            onChange={(event) => handleFichaFieldChange(field, event.target.value)}
          >
            <option value="">{source === "estado" && !field.editable ? "Sin valor" : "Seleccionar"}</option>
            {options.map((option) => (
              <option key={`${field.key}-${option.value}`} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
          <span className="crm-postventa-select-icon" aria-hidden="true">
            <FontAwesomeIcon icon={faChevronDown} />
          </span>
        </div>
      );
    }

    return (
      <Form.Control
        className={`crm-postventa-input ${isReadOnly ? "is-readonly" : ""}`}
        as={field.inputType === "textarea" ? "textarea" : undefined}
        rows={field.inputType === "textarea" ? 4 : undefined}
        type={field.inputType === "textarea" ? undefined : field.inputType || "text"}
        value={value}
        readOnly={isReadOnly}
        disabled={isReadOnly}
        onChange={(event) => handleFichaFieldChange(field, event.target.value)}
      />
    );
  }, [fichaCatalogos, fichaEditDraft, handleFichaFieldChange]);

  const handleCrearGestion = async (event) => {
    event.preventDefault();
    if (!clienteSeleccionadoId) {
      return;
    }

    if (!gestionDraft.reglaId) {
      Swal.fire("Gestión", "Seleccione un motivo/acción para guardar la gestión.", "info");
      return;
    }

    if (!gestionDraft.estadoGestion) {
      Swal.fire("GestiÃ³n", "Seleccione un estado para la gestiÃ³n.", "info");
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoGestion: true }));
    try {
      await crmPostventaApi.crearGestion(
        clienteSeleccionadoId,
        Number(gestionDraft.reglaId),
        gestionDraft.estadoGestion,
        gestionDraft.observacion,
        gestionDraft.fechaGestion,
        gestionDraft.fechaProximoContacto,
        resolveMotivoCodigoFromRegla(gestiones?.reglas || [], gestionDraft.reglaId)
      );

      Swal.fire("Ok", "La gestión se guardó correctamente.", "success");
      setGestionDraft((prev) => ({ ...prev, observacion: "", fechaProximoContacto: "" }));
      await cargarModulo(clienteSeleccionadoId, analyticsRange);
      navigate("/administracion/crm-postventa/gestion");
    } catch (error) {
      console.error("Error guardando gestión:", error);
      Swal.fire("Error", error.message || "No se pudo guardar la gestión.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoGestion: false }));
    }
  };

  const handleRegistrarRollo = () => {
    if (!clienteSeleccionadoId) {
      return;
    }

    setRolloDraft({
      fechaEntrega: makeToday(),
      cantidad: "",
      observacion: "",
    });
    setRolloModalOpen(true);
  };

  const handleGuardarRollo = async (event) => {
    event.preventDefault();

    if (!clienteSeleccionadoId) {
      return;
    }

    const cantidad = Number(rolloDraft.cantidad);
    if (!rolloDraft.fechaEntrega) {
      Swal.fire("Rollos", "La fecha de entrega es obligatoria.", "info");
      return;
    }

    if (!Number.isFinite(cantidad) || cantidad <= 0) {
      Swal.fire("Rollos", "La cantidad es obligatoria y debe ser mayor a 0.", "info");
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoRollo: true }));
    try {
      await crmPostventaApi.registrarRollo(
        {
          clienteId: clienteSeleccionadoId,
          clienteKey: gestiones?.rollos?.clienteKey || crmPostventaApi.buildClienteKey(clienteHeader),
          cuit: clienteHeader.cuit,
          nDeComercio: clienteHeader.nDeComercio,
        },
        rolloDraft.fechaEntrega,
        cantidad,
        rolloDraft.observacion
      );

      setRolloModalOpen(false);
      setRolloDraft({
        fechaEntrega: makeToday(),
        cantidad: "",
        observacion: "",
      });
      await cargarModulo(clienteSeleccionadoId, analyticsRange);

      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Entrega registrada correctamente.",
        showConfirmButton: false,
        timer: 2400,
        timerProgressBar: true,
      });
    } catch (error) {
      Swal.fire("Error", error.message || "No se pudo registrar la entrega.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoRollo: false }));
    }
  };

  const showFacturacionDetail = (row) => {
    setFacturacionDetalle(row);
  };

  const aplicarFiltrosAlertas = useCallback(() => {
    const nextFilters = {
      ...alertasFiltros,
      search: alertasSearchDraft,
    };

    setAlertasFiltros(nextFilters);
    cargarAlertas(nextFilters);
  }, [alertasFiltros, alertasSearchDraft, cargarAlertas]);

  const handleAlertasSearchKeyDown = useCallback((event) => {
    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();
    aplicarFiltrosAlertas();
  }, [aplicarFiltrosAlertas]);

  const abrirGestionFacturacion = useCallback((row, defaults = {}) => {
    if (!row?.clienteId) {
      Swal.fire("Facturacion", "La fila seleccionada no tiene un cliente asociado para registrar la gestion.", "info");
      return;
    }

    setFacturacionAccionItem(null);
    setFacturacionGestionItem(row);
    setFacturacionGestionDraft({
      clienteId: row.clienteId,
      reglaId: manualGestionRules?.[0]?.id ? String(manualGestionRules[0].id) : "",
      estadoGestion: defaults.estadoGestion || "Pendiente",
      observacion:
        defaults.observacion ||
        `Gestion desde Facturacion. Comercio ${row.comercio || "-"} · Terminal ${row.terminal || "-"} · Estado ${row.estadoAlerta || "-"}.`,
      fechaGestion: `${makeToday()}T09:00`,
      fechaProximoContacto: "",
    });
  }, [gestiones?.reglas]);

  const copiarCuitFacturacion = useCallback(async (row) => {
    const cuit = String(row?.cuit || "").trim();
    if (!cuit) {
      Swal.fire("Facturacion", "La fila seleccionada no tiene CUIT para copiar.", "info");
      return;
    }

    try {
      await navigator.clipboard.writeText(cuit);
      setFacturacionAccionItem(null);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "CUIT copiado al portapapeles.",
        showConfirmButton: false,
        timer: 1800,
        timerProgressBar: true,
      });
    } catch (error) {
      Swal.fire("Facturacion", "No se pudo copiar el CUIT.", "error");
    }
  }, []);

  const ejecutarAccionFacturacion = useCallback((accion, row) => {
    if (!row) {
      return;
    }

    switch (accion) {
      case "detalle":
        setFacturacionAccionItem(null);
        showFacturacionDetail(row);
        return;
      case "filtrar":
        setFacturacionAccionItem(null);
        setFacturacionFiltroComercio(row.comercio || row.terminal || "");
        return;
      case "copiarCuit":
        copiarCuitFacturacion(row);
        return;
      case "registrarGestion":
        abrirGestionFacturacion(row, {
          observacion: `Gestion desde Facturacion. Comercio ${row.comercio || "-"} · Terminal ${row.terminal || "-"} · Estado ${row.estadoAlerta || "-"}.`,
        });
        return;
      case "seguimiento":
        abrirGestionFacturacion(row, {
          estadoGestion: "Pendiente",
          observacion: `Seguimiento pendiente desde Facturacion para comercio ${row.comercio || "-"} y terminal ${row.terminal || "-"}.`,
        });
        return;
      default:
        return;
    }
  }, [abrirGestionFacturacion, copiarCuitFacturacion]);

  const guardarGestionDesdeFacturacion = async (event) => {
    event.preventDefault();

    if (!facturacionGestionDraft.clienteId || !facturacionGestionDraft.reglaId || !facturacionGestionDraft.estadoGestion) {
      Swal.fire("Facturacion", "Complete regla y estado antes de guardar la gestion.", "info");
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoGestion: true }));
    try {
      await crmPostventaApi.crearGestion(
        facturacionGestionDraft.clienteId,
        Number(facturacionGestionDraft.reglaId),
        facturacionGestionDraft.estadoGestion,
        facturacionGestionDraft.observacion,
        facturacionGestionDraft.fechaGestion,
        facturacionGestionDraft.fechaProximoContacto,
        "FALTA_FACTURACION"
      );

      Swal.fire("Ok", "La gestion se registro correctamente.", "success");
      setFacturacionGestionItem(null);
      await cargarModulo(clienteSeleccionadoId, analyticsRange);
    } catch (error) {
      console.error("Error guardando gestion desde facturacion:", error);
      Swal.fire("Error", error.message || "No se pudo registrar la gestion.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoGestion: false }));
    }
  };

  const abrirClienteDesdeAlerta = useCallback((row) => {
    if (!row?.clienteId) {
      Swal.fire("Alertas", "La alerta no tiene un cliente asociado para abrir la ficha.", "info");
      return;
    }

    localStorage.setItem(STORAGE_KEY, String(row.clienteId));
    setClienteSeleccionadoId(row.clienteId);
    setClientePendiente(null);
    setFacturacionDetalle(null);
    setTimelineDetalle(null);
    navigate("/administracion/crm-postventa/ficha");
  }, [navigate]);

  const goToGestion = (row, observacion) => {
    if (row?.clienteId) {
      localStorage.setItem(STORAGE_KEY, String(row.clienteId));
      setClienteSeleccionadoId(row.clienteId);
      setClientePendiente(null);
      setFacturacionDetalle(null);
      setTimelineDetalle(null);
    }

    setGestionDraft((prev) => ({
      ...prev,
      estadoGestion: prev.estadoGestion || "Pendiente",
      observacion,
    }));
    navigate("/administracion/crm-postventa/gestion");
  };

  const abrirAccionAlerta = useCallback((row, estadoGestion, observacionBase = "") => {
    if (!row?.clienteId) {
      Swal.fire("Alertas", "La alerta no tiene un cliente asociado para registrar una gestión.", "info");
      return;
    }

    setAlertaSeleccionada(row);
    setAlertaGestionDraft({
      clienteId: row.clienteId,
      reglaId: manualAlertaRules?.[0]?.id ? String(manualAlertaRules[0].id) : "",
      estadoGestion,
      observacion: observacionBase || buildSuggestedObservation(row, estadoGestion),
      fechaGestion: `${makeToday()}T09:00`,
      fechaProximoContacto: "",
    });
  }, [manualAlertaRules]);

  const ejecutarAccionAlerta = useCallback((accion, row) => {
    if (!row) {
      return;
    }

    setAlertaAccionItem(null);

    switch (accion) {
      case "verCliente":
        abrirClienteDesdeAlerta(row);
        return;
      case "registrarGestion":
        goToGestion(row, buildSuggestedObservation(row, "Pendiente"));
        return;
      case "contactado":
        abrirAccionAlerta(row, "Contactado", buildSuggestedObservation(row, "Contactado"));
        return;
      case "posponer":
        abrirAccionAlerta(row, "Pendiente", buildSuggestedObservation(row, "Pendiente"));
        return;
      case "cerrar":
        abrirAccionAlerta(row, "Cerrado", buildSuggestedObservation(row, "Cerrado"));
        return;
      case "escalar":
        abrirAccionAlerta(row, "Escalado", buildSuggestedObservation(row, "Escalado"));
        return;
      case "copiarMensaje":
        copiarMensajeSugerido(row, "Alertas");
        return;
      case "whatsapp":
        abrirWhatsappSugerido(row, "Alertas");
        return;
      default:
        return;
    }
  }, [abrirAccionAlerta, abrirClienteDesdeAlerta, abrirWhatsappSugerido, copiarMensajeSugerido]);

  const guardarGestionDesdeAlerta = async (event) => {
    event.preventDefault();

    if (!alertaGestionDraft.clienteId || !alertaGestionDraft.reglaId || !alertaGestionDraft.estadoGestion) {
      Swal.fire("Alertas", "Complete regla y estado antes de guardar la gestión.", "info");
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoAlertaGestion: true }));
    try {
      await crmPostventaApi.crearGestion(
        alertaGestionDraft.clienteId,
        Number(alertaGestionDraft.reglaId),
        alertaGestionDraft.estadoGestion,
        alertaGestionDraft.observacion,
        alertaGestionDraft.fechaGestion,
        alertaGestionDraft.fechaProximoContacto,
        resolveMotivoCodigoFromAlerta(alertaSeleccionada)
      );

      Swal.fire("Ok", "La gestión de alerta se registró correctamente.", "success");
      setAlertaSeleccionada(null);
      await cargarAlertas(alertasFiltros);
      if (clienteSeleccionadoId === alertaGestionDraft.clienteId) {
        await cargarModulo(clienteSeleccionadoId, analyticsRange);
      }
    } catch (error) {
      console.error("Error guardando gestión desde alerta:", error);
      Swal.fire("Error", error.message || "No se pudo registrar la gestión.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoAlertaGestion: false }));
    }
  };

  const renderFicha = () => {
    if (cargas.ficha) {
      return <div className="crm-postventa-subtle"><Spinner animation="border" size="sm" className="me-2" />Cargando ficha...</div>;
    }

    if (errores.ficha) {
      return <Alert variant="danger">{errores.ficha}</Alert>;
    }

    if (!ficha) {
      return <Alert variant="secondary">No hay ficha disponible para este cliente.</Alert>;
    }

    const header = ficha.header || clienteHeader;

    return (
      <div className="crm-postventa-ficha-wrap">
        <div className="crm-postventa-panel-title mb-3">
          <h3></h3>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={abrirEdicionFicha}
            disabled={!ficha?.edicion?.permiteEdicion || !fichaCamposEdicion.length}
          >
            <FontAwesomeIcon icon={faUserPen} className="me-2" />
            Editar datos
          </Button>
        </div>
        <div className={`crm-postventa-ficha-resumen ${darkMode ? "is-dark" : ""}`}>
          <div className="crm-postventa-ficha-resumen-item">
            <span className="crm-postventa-card-label">Estado</span>
            <PostventaBadge
              value={header.estado || "-"}
              tone={
                String(header.estado).toUpperCase() === "ALTA"
                  ? "success"
                  : String(header.estado).toUpperCase() === "BAJA"
                    ? "danger"
                    : "default"
              }
            />
          </div>
          <div className="crm-postventa-ficha-resumen-item">
            <span className="crm-postventa-card-label">CUIT</span>
            <strong>{header.cuit || "-"}</strong>
          </div>
          <div className="crm-postventa-ficha-resumen-item">
            <span className="crm-postventa-card-label">Terminal</span>
            <strong>{header.nDeTerminal || "-"}</strong>
          </div>
          <div className="crm-postventa-ficha-resumen-item">
            <span className="crm-postventa-card-label">Comercio</span>
            <strong>{header.nDeComercio || "-"}</strong>
          </div>
          <div className="crm-postventa-ficha-resumen-item">
            <span className="crm-postventa-card-label">Legajo</span>
            <strong>{header.legajo || "-"}</strong>
          </div>
          <div className="crm-postventa-ficha-resumen-item">
            <span className="crm-postventa-card-label">Producto</span>
            <strong>{header.producto || "-"}</strong>
          </div>
        </div>

        <div className="crm-postventa-ficha-grid">
          <section className={`crm-postventa-ficha-block ${darkMode ? "is-dark" : ""}`}>
            <h4>Identificación</h4>
            <DataList items={ficha.datosGenerales} />
          </section>
          <section className={`crm-postventa-ficha-block ${darkMode ? "is-dark" : ""}`}>
            <h4>Contacto</h4>
            <DataList items={ficha.contacto} />
          </section>
          <section className={`crm-postventa-ficha-block ${darkMode ? "is-dark" : ""}`}>
            <h4>Fiscal</h4>
            <DataList items={ficha.fiscal} />
          </section>
          <section className={`crm-postventa-ficha-block ${darkMode ? "is-dark" : ""}`}>
            <h4>Bancario / operativo</h4>
            <DataList items={ficha.operativo} />
          </section>
        </div>

        <section className={`crm-postventa-ficha-block mt-3 ${darkMode ? "is-dark" : ""}`}>
          <h4>Historial de cambios</h4>
          {ficha?.historialCambios?.length ? (
            <div className="crm-postventa-table-wrap">
              <Table className="crm-postventa-table mb-0" responsive hover>
                <thead className="table-light">
                  <tr>
                    <th>Fecha</th>
                    <th>Usuario</th>
                    <th>Tipo</th>
                    <th>Valores modificados</th>
                  </tr>
                </thead>
                <tbody>
                  {ficha.historialCambios.map((item, index) => (
                    <tr key={`ficha-hist-${index}`}>
                      <td>{item.fecha || "-"}</td>
                      <td>{item.usuario || "-"}</td>
                      <td>{item.tipo || "-"}</td>
                      <td>{Array.isArray(item.valoresModificados) && item.valoresModificados.length ? item.valoresModificados.join(" | ") : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <Alert variant="secondary" className="mb-0">No hay modificaciones CRM registradas para esta ficha.</Alert>
          )}
        </section>

        <Modal
          show={fichaEditOpen}
          onHide={() => setFichaEditOpen(false)}
          centered
          dialogClassName="crm-postventa-ficha-workspace"
          contentClassName={`crm-postventa-ficha-workspace-content ${darkMode ? "is-dark" : ""}`}
        >
          <Form className="crm-postventa-ficha-workspace-form" onSubmit={guardarEdicionFicha}>
            <Modal.Header className="crm-postventa-ficha-workspace-header">
              <div className="crm-postventa-ficha-workspace-topbar">
                <div>
                  <span className="crm-postventa-ficha-workspace-kicker">CRM Postventa</span>
                  <Modal.Title>Editar datos del cliente</Modal.Title>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Cerrar"
                  onClick={() => setFichaEditOpen(false)}
                />
              </div>
              <div className={`crm-postventa-ficha-hero ${darkMode ? "is-dark" : ""}`}>
                <div className="crm-postventa-ficha-hero-main">
                  <span className="crm-postventa-ficha-hero-label">Cliente</span>
                  <strong>{clienteHeader.nombreDeFantasia || clienteHeader.razonSocial || "Cliente"}</strong>
                </div>
                <div className="crm-postventa-ficha-hero-grid">
                  <div className="crm-postventa-ficha-hero-item">
                    <span>CUIT</span>
                    <strong>{clienteHeader.cuit || "-"}</strong>
                  </div>
                  <div className="crm-postventa-ficha-hero-item">
                    <span>Terminal</span>
                    <strong>{clienteHeader.nDeTerminal || "-"}</strong>
                  </div>
                  <div className="crm-postventa-ficha-hero-item">
                    <span>Comercio</span>
                    <strong>{clienteHeader.nDeComercio || "-"}</strong>
                  </div>
                </div>
              </div>
            </Modal.Header>
            <Modal.Body className="crm-postventa-ficha-workspace-body">
              <div className="crm-postventa-ficha-workspace-intro">
                <div>
                  <h4>Edicion completa de ficha</h4>
                  <p>Organizamos los datos por secciones para editar en una grilla comoda y evitar una lista vertical eterna.</p>
                </div>
              </div>

              {fichaCatalogosLoading ? (
                <Alert variant="secondary" className="mb-0">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Cargando opciones de edición...
                </Alert>
              ) : (
                <div className="crm-postventa-ficha-workspace-sections">
                  {Object.entries(fichaCamposAgrupados).map(([section, items]) => (
                    <section key={section} className={`crm-postventa-edit-section-card ${darkMode ? "is-dark" : ""}`}>
                      <div className="crm-postventa-edit-section-card-head">
                        <div>
                          <h5>{section}</h5>
                          <p>{items.length} campo(s) disponibles en esta seccion.</p>
                        </div>
                        <span>{items.length}</span>
                      </div>
                      <div className="crm-postventa-edit-grid">
                        {items.map((item) => (
                          <Form.Group key={item.key} className="crm-postventa-edit-field">
                            <Form.Label>
                              <span>{item.label}</span>
                              {!item.editable ? <small>Solo lectura</small> : null}
                            </Form.Label>
                            {renderFichaEditField(item)}
                            {item.readOnlyReason ? (
                              <Form.Text className="crm-postventa-field-help">{item.readOnlyReason}</Form.Text>
                            ) : null}
                          </Form.Group>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              )}
            </Modal.Body>
            <Modal.Footer className="crm-postventa-ficha-workspace-footer">
              <Button type="button" variant="outline-secondary" onClick={() => setFichaEditOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" variant="success" disabled={cargas.guardandoFicha || fichaCatalogosLoading}>
                {cargas.guardandoFicha ? "Guardando..." : "Guardar cambios"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    );
  };

  const renderFacturacion = () => {
    if (cargas.facturacion) {
      return <div className="crm-postventa-subtle"><Spinner animation="border" size="sm" className="me-2" />Cargando facturación...</div>;
    }

    if (errores.facturacion) {
      return <Alert variant="danger">{errores.facturacion}</Alert>;
    }

    if (!facturacion?.tieneDatosReales) {
      return <Alert variant="secondary">{facturacion?.message || "No hay datos reales de facturación disponibles para este cliente."}</Alert>;
    }

    return (
      <div className="crm-postventa-alert-layout d-grid gap-3">
        <div className="crm-postventa-actions-row">
          <div className="crm-postventa-range">
            <Form.Control
              type="date"
              value={analyticsRange.desde}
              onChange={(event) => setAnalyticsRange((prev) => ({ ...prev, desde: event.target.value }))}
            />
            <Form.Control
              type="date"
              value={analyticsRange.hasta}
              onChange={(event) => setAnalyticsRange((prev) => ({ ...prev, hasta: event.target.value }))}
            />
            <Button variant="outline-success" onClick={() => cargarModulo(clienteSeleccionadoId, analyticsRange)}>
              <FontAwesomeIcon className="me-2" icon={faArrowsRotate} />
              Actualizar
            </Button>
          </div>
          {facturacionFiltroComercio ? (
            <Button variant="outline-secondary" onClick={() => setFacturacionFiltroComercio("")}>
              Ver todo el grupo
            </Button>
          ) : null}
        </div>

        <div className="crm-postventa-card-grid crm-postventa-alert-kpis">
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Fecha referencia</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {facturacion.resumen.fechaReferencia || facturacion.resumen.fechaHasta || "-"}
            </div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Monto hoy</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {formatCurrencyDetailed(facturacion.resumen.montoHoy)}
            </div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Ayer</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {formatCurrencyDetailed(facturacion.resumen.montoAyer)}
            </div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Promedio 3 días</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {formatCurrencyDetailed(facturacion.resumen.promedio3Dias)}
            </div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Promedio 7 días</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {formatCurrencyDetailed(facturacion.resumen.promedio7Dias)}
            </div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Terminales</span>
            <div className="crm-postventa-card-value">{facturacion.resumen.terminales}</div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Con facturación</span>
            <div className="crm-postventa-card-value">{facturacion.resumen.conFacturacion}</div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Sin facturación</span>
            <div className="crm-postventa-card-value">{facturacion.resumen.sinFacturacion}</div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Caída fuerte</span>
            <div className="crm-postventa-card-value">{facturacion.resumen.caidaFuerte}</div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Última actividad</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {facturacion.resumen.ultimaActividad || "Sin datos"}
            </div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">CUIT del grupo</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {facturacion.resumen.cuitDelGrupo || "-"}
            </div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Variación vs ayer</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {percent(facturacion.resumen.variacionVsAyer)}
            </div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Variación vs 7 días</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {percent(facturacion.resumen.variacionVs7Dias)}
            </div>
          </div>
        </div>

        <div className="crm-postventa-insights crm-postventa-alert-insights">
          <article className="crm-postventa-insight">
            <span className="crm-postventa-card-label">Motivo principal</span>
            <strong>{facturacion.insights.motivoPrincipal}</strong>
          </article>
          <article className="crm-postventa-insight">
            <span className="crm-postventa-card-label">Insight</span>
            <strong>{facturacion.insights.insight}</strong>
          </article>
          <article className="crm-postventa-insight">
            <span className="crm-postventa-card-label">Acción sugerida</span>
            <strong>{facturacion.insights.accionSugerida}</strong>
          </article>
        </div>

        <div className="crm-postventa-table-wrap crm-postventa-table-wrap-scroll crm-postventa-table-wrap-safe">
          <Table className="crm-postventa-table" responsive hover>
            <thead className="table-light">
              <tr>
                <th>CUIT</th>
                <th>Terminal</th>
                <th>Comercio</th>
                <th>Estado</th>
                <th>Hoy</th>
                <th>Ayer</th>
                <th>Prom. 3d</th>
                <th>Prom. 7d</th>
                <th>Var. vs ayer</th>
                <th>Var. vs 7d</th>
                <th>Días sin fact.</th>
                <th>Última actividad</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filasFacturacion.map((row) => (
                <tr key={`${row.cuit}-${row.terminal}-${row.comercio}`}>
                  <td className="text-nowrap">{row.cuit || "-"}</td>
                  <td>{row.terminal || "-"}</td>
                  <td>{row.comercio || "-"}</td>
                  <td>
                    <PostventaBadge
                      value={row.estadoAlerta}
                      tone={
                        row.estadoAlerta === "OK"
                          ? "success"
                          : row.estadoAlerta === "Inactivo" || row.estadoAlerta === "Caída vs promedio"
                            ? "warning"
                            : "danger"
                      }
                    />
                  </td>
                  <td className="text-nowrap">{formatCurrencyDetailed(row.montoHoy)}</td>
                  <td className="text-nowrap">{formatCurrencyDetailed(row.montoAyer)}</td>
                  <td className="text-nowrap">{formatCurrencyDetailed(row.promedio3Dias)}</td>
                  <td className="text-nowrap">{formatCurrencyDetailed(row.promedio7Dias)}</td>
                  <td className="text-nowrap">{percent(row.variacionVsAyer)}</td>
                  <td className="text-nowrap">{percent(row.variacion)}</td>
                  <td>{row.diasGrupoSinFacturar != null ? row.diasGrupoSinFacturar : "-"}</td>
                  <td className="text-nowrap">{row.ultimaActividad || "-"}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="crm-postventa-action-button"
                      onClick={() => setFacturacionAccionItem(row)}
                    >
                      Acciones
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        <Modal show={Boolean(facturacionAccionItem)} onHide={() => setFacturacionAccionItem(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Acciones de facturacion</Modal.Title>
          </Modal.Header>
          <Modal.Body className="d-grid gap-2">
            <div className="crm-postventa-subtle crm-postventa-action-summary p-0">
              <strong>{facturacionAccionItem?.nombreDeFantasia || "Cliente"}</strong>
              <span>Comercio {facturacionAccionItem?.comercio || "-"}</span>
              <span>Terminal {facturacionAccionItem?.terminal || "-"}</span>
              <span>CUIT {facturacionAccionItem?.cuit || "-"}</span>
            </div>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionFacturacion("detalle", facturacionAccionItem)}>
              Ver detalle
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionFacturacion("filtrar", facturacionAccionItem)}>
              Ver solo este comercio
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionFacturacion("copiarCuit", facturacionAccionItem)}>
              Copiar CUIT
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionFacturacion("registrarGestion", facturacionAccionItem)}>
              Registrar gestion
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionFacturacion("seguimiento", facturacionAccionItem)}>
              Marcar seguimiento
            </Button>
          </Modal.Body>
        </Modal>

        <Modal show={Boolean(facturacionGestionItem)} onHide={() => setFacturacionGestionItem(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Registrar gestion desde Facturacion</Modal.Title>
          </Modal.Header>
          <Form onSubmit={guardarGestionDesdeFacturacion}>
            <Modal.Body>
              <div className="crm-postventa-subtle crm-postventa-action-summary p-0 mb-3">
                <strong>{facturacionGestionItem?.nombreDeFantasia || "Cliente"}</strong>
                <span>Comercio {facturacionGestionItem?.comercio || "-"}</span>
                <span>Terminal {facturacionGestionItem?.terminal || "-"}</span>
                <span>CUIT {facturacionGestionItem?.cuit || "-"}</span>
              </div>
              <div className="crm-postventa-form-grid">
                <Form.Group>
                  <Form.Label>Regla / accion</Form.Label>
                  <Form.Select
                    value={facturacionGestionDraft.reglaId}
                    onChange={(event) => setFacturacionGestionDraft((prev) => ({ ...prev, reglaId: event.target.value }))}
                  >
                    <option value="">Seleccionar</option>
                    {manualGestionRules.map((regla) => (
                      <option key={regla.id} value={regla.id}>{regla.aliasVista} · {regla.nuevoEstado || "Sin estado"}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Estado</Form.Label>
                  <Form.Select
                    value={facturacionGestionDraft.estadoGestion}
                    onChange={(event) => setFacturacionGestionDraft((prev) => ({ ...prev, estadoGestion: event.target.value }))}
                  >
                    {(gestiones?.estadosGestion?.length ? gestiones.estadosGestion : GESTION_STATES).map((item) => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Fecha / hora</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={facturacionGestionDraft.fechaGestion}
                    onChange={(event) => setFacturacionGestionDraft((prev) => ({ ...prev, fechaGestion: event.target.value }))}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Proximo contacto</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={facturacionGestionDraft.fechaProximoContacto}
                    onChange={(event) => setFacturacionGestionDraft((prev) => ({ ...prev, fechaProximoContacto: event.target.value }))}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Observacion</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={facturacionGestionDraft.observacion}
                    onChange={(event) => setFacturacionGestionDraft((prev) => ({ ...prev, observacion: event.target.value }))}
                  />
                </Form.Group>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => setFacturacionGestionItem(null)}>
                Cancelar
              </Button>
              <Button type="submit" variant="success" disabled={cargas.guardandoGestion}>
                {cargas.guardandoGestion ? "Guardando..." : "Guardar gestion"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {facturacionDetalle ? (
          <div className="crm-postventa-detail-grid">
            <section className={`crm-postventa-detail-card ${darkMode ? "is-dark" : ""}`}>
              <div className="crm-postventa-panel-title">
                <h4>Detalle de facturación</h4>
                <Button variant="outline-secondary" size="sm" onClick={() => setFacturacionDetalle(null)}>
                  Cerrar
                </Button>
              </div>
              <p className="mb-3">
                {facturacionDetalle.nombreDeFantasia || "Cliente"} · Terminal {facturacionDetalle.terminal || "-"} · Comercio {facturacionDetalle.comercio || "-"}
              </p>
              <div className="crm-postventa-detail-table-wrap">
                <Table className="crm-postventa-table mb-0" hover>
                  <thead className="table-light">
                    <tr>
                      <th>Fecha</th>
                      <th>Total bruto</th>
                      <th>Operaciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {facturacionDetalle.serieDiaria?.map((item) => (
                      <tr key={`${facturacionDetalle.terminal}-${item.fecha}`}>
                        <td>{item.fecha}</td>
                        <td>{formatCurrencyDetailed(item.totalBruto)}</td>
                        <td>{item.cantidadOperaciones || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </section>
            <aside className={`crm-postventa-detail-card ${darkMode ? "is-dark" : ""}`}>
              <h4 className="mb-3">Resumen rápido</h4>
              <DataList
                items={[
                  { label: "Estado", value: facturacionDetalle.estadoAlerta, isBadge: true, tone: facturacionDetalle.estadoAlerta === "OK" ? "success" : "danger" },
                  { label: "Monto hoy", value: formatCurrencyDetailed(facturacionDetalle.montoHoy) },
                  { label: "Promedio 7 días", value: formatCurrencyDetailed(facturacionDetalle.promedio7Dias) },
                  { label: "Variación", value: percent(facturacionDetalle.variacion) },
                  { label: "Última actividad", value: facturacionDetalle.ultimaActividad || "Sin datos" },
                  { label: "Acción sugerida", value: facturacionDetalle.accionSugerida || "-" },
                ]}
              />
            </aside>
          </div>
        ) : null}
      </div>
    );
  };

  const renderAlertas = () => {
    if (cargas.alertas) {
      return <div className="crm-postventa-subtle"><Spinner animation="border" size="sm" className="me-2" />Cargando alertas...</div>;
    }

    if (errores.alertas) {
      return <Alert variant="danger">{errores.alertas}</Alert>;
    }

    if (!alertas) {
      return <Alert variant="secondary">No hay alertas disponibles para mostrar.</Alert>;
    }

    return (
      <div className="crm-postventa-alert-layout d-grid gap-3">
        <div className="crm-postventa-actions-row">
          <div className="crm-postventa-range crm-postventa-alert-filters">
            <Form.Control
              type="date"
              className="crm-postventa-filter-control crm-postventa-filter-date"
              value={alertasFiltros.fecha}
              onChange={(event) => setAlertasFiltros((prev) => ({ ...prev, fecha: event.target.value }))}
            />
            <Form.Select
              className="crm-postventa-filter-control crm-postventa-filter-select"
              value={alertasFiltros.prioridad}
              onChange={(event) => setAlertasFiltros((prev) => ({ ...prev, prioridad: event.target.value }))}
            >
              <option value="">Todas las prioridades</option>
              <option value="Crítica">Crítica</option>
              <option value="Alta">Alta</option>
              <option value="Media">Media</option>
              <option value="Baja">Baja</option>
            </Form.Select>
            <Form.Select
              className="crm-postventa-filter-control crm-postventa-filter-select"
              value={alertasFiltros.estado}
              onChange={(event) => setAlertasFiltros((prev) => ({ ...prev, estado: event.target.value }))}
            >
              <option value="">Todos los estados</option>
              {(alertas.estadosGestion?.length ? alertas.estadosGestion : GESTION_STATES).map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </Form.Select>
            <Form.Control
              type="search"
              className="crm-postventa-filter-control crm-postventa-filter-search"
              placeholder="Buscar por comercio, CUIT, provincia o responsable"
              value={alertasSearchDraft}
              onChange={(event) => setAlertasSearchDraft(event.target.value)}
              onKeyDown={handleAlertasSearchKeyDown}
            />
            <Button className="crm-postventa-filter-button" variant="outline-success" onClick={aplicarFiltrosAlertas}>
              <FontAwesomeIcon className="me-2" icon={faArrowsRotate} />
              Actualizar
            </Button>
          </div>
        </div>

        <div className="crm-postventa-card-grid crm-postventa-alert-kpis">
          {(alertas.kpis || []).map((item) => (
            <article className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`} key={item.label}>
              <span className="crm-postventa-card-label">{item.label}</span>
              <div className="crm-postventa-card-value">{item.value}</div>
              <p className="crm-postventa-card-note mb-0">{item.description}</p>
            </article>
          ))}
        </div>

        <div className="crm-postventa-insights crm-postventa-alert-insights">
          <article className="crm-postventa-insight">
            <span className="crm-postventa-card-label">Insight general</span>
            <strong>{alertas.insightGeneral || "Sin datos"}</strong>
          </article>
          <article className="crm-postventa-insight">
            <span className="crm-postventa-card-label">Alerta destacada</span>
            <strong>{alertas.alertaDestacada || "Sin datos"}</strong>
          </article>
          <article className="crm-postventa-insight">
            <span className="crm-postventa-card-label">Acción sugerida</span>
            <strong>{alertas.accionGeneral || "Sin datos"}</strong>
          </article>
        </div>

        <div className="crm-postventa-alert-blocks">
          <section className={`crm-postventa-panel crm-postventa-alert-critical-panel ${darkMode ? "is-dark" : ""}`}>
            <div className="crm-postventa-panel-title">
              <h3>Alertas críticas</h3>
            </div>
            <div className="crm-postventa-alert-list crm-postventa-alert-list-scroll">
              {alertas.alertasCriticas?.length ? alertas.alertasCriticas.map((item, index) => (
                <article className="crm-postventa-alert-list-item" key={`${item.cuit}-${item.tipoAlerta}-${index}`}>
                  <div className="d-flex justify-content-between gap-3 flex-wrap">
                    <strong>{item.comercio || "Sin comercio"} · {item.tipoAlerta}</strong>
                    <PostventaBadge value={item.prioridad} tone={toneForPriority(item.prioridad)} />
                  </div>
                  <div className="crm-postventa-inline-badges">
                    <PostventaBadge value={`Segmento ${item.segmentoComercial || "Nuevo"}`} tone={toneForSegment(item.segmentoComercial)} />
                    <PostventaBadge value={item.patronFacturacion || "NUEVO"} tone={toneForPattern(item.patronFacturacion)} />
                  </div>
                  <p className="mb-0">{item.resumen || "Sin resumen."}</p>
                  <div className="crm-postventa-alert-table-subline">{item.motivoPrioridad || "Sin motivo de prioridad."}</div>
                </article>
              )) : <Alert variant="secondary" className="mb-0">No hay alertas críticas para la fecha seleccionada.</Alert>}
            </div>
          </section>

          <section className={`crm-postventa-panel ${darkMode ? "is-dark" : ""}`}>
            <div className="crm-postventa-panel-title">
              <h3>Bloques</h3>
            </div>
            <div className="crm-postventa-alert-pill-row">
              <span className="crm-postventa-chip">Facturación {alertas.alertasFacturacion?.length || 0}</span>
              <span className="crm-postventa-chip">Gestión {alertas.alertasGestion?.length || 0}</span>
              <span className="crm-postventa-chip">Rollos {alertas.alertasRollos?.length || 0}</span>
              <span className="crm-postventa-chip">Fecha {alertas.fechaReferencia || "-"}</span>
            </div>
            {alertas?.generacionAutomatica ? (
              <p className="mb-0 mt-3">
                Generación automática: +{alertas.generacionAutomatica.creadasFacturacion || 0} facturación, +{alertas.generacionAutomatica.creadasRollos || 0} rollos, vencidos {alertas.generacionAutomatica.proximosContactosVencidos || 0}.
              </p>
            ) : null}
            <p className="mb-0 mt-3">{alertas.rollosMessage || "Sin novedad de rollos."}</p>
          </section>
        </div>

        <section className={`crm-postventa-panel crm-postventa-alert-queue ${darkMode ? "is-dark" : ""}`}>
          <div className="crm-postventa-panel-title">
            <h3>Alertas operativas</h3>
            <span className="crm-postventa-card-label">
              Mostrando {alertasOperativasFiltradas.length} de {alertas.filas?.length || 0} alertas
            </span>
          </div>
          <div className="crm-postventa-alert-queue-filters">
            <Form.Control
              type="search"
              className="crm-postventa-filter-control"
              placeholder="Filtrar alertas operativas por comercio, CUIT, terminal, provincia, tipo, responsable o estado"
              value={alertasOperativasSearch}
              onChange={(event) => setAlertasOperativasSearch(event.target.value)}
            />
          </div>
          <div className="crm-postventa-alert-queue-scroll">
            <div className="crm-postventa-alert-queue-list">
            <Table className="crm-postventa-table crm-postventa-table-alertas mb-0" hover>
            <thead className="table-light">
              <tr>
                <th>Prioridad / estado</th>
                <th>Comercio</th>
                <th>Alerta</th>
                <th>Facturación</th>
                <th>Gestión</th>
                <th className="crm-postventa-action-cell">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {alertasOperativasFiltradas.length ? alertasOperativasFiltradas.map((item, index) => (
                <tr key={`${item.cuit}-${item.tipoAlerta}-${index}`}>
                  <td data-label="Prioridad / estado">
                    <div className="crm-postventa-alert-table-stack">
                      <PostventaBadge value={item.prioridad} tone={toneForPriority(item.prioridad)} />
                      <PostventaBadge value={item.estado} tone={toneForGestionState(item.estado)} />
                      <PostventaBadge value={`Score ${item.scorePrioridad || 0}`} tone={toneForPriority(item.prioridad)} />
                    </div>
                  </td>
                  <td className="crm-postventa-alert-table-commerce" data-label="Comercio">
                    <strong className="crm-postventa-alert-table-title">{item.comercio || "Sin comercio"}</strong>
                    <div className="crm-postventa-inline-badges">
                      <PostventaBadge value={`Segmento ${item.segmentoComercial || "Nuevo"}`} tone={toneForSegment(item.segmentoComercial)} />
                      <PostventaBadge value={item.patronFacturacion || "NUEVO"} tone={toneForPattern(item.patronFacturacion)} />
                    </div>
                    <div className="crm-postventa-alert-table-subline">CUIT: {item.cuit || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Terminal: {item.terminal || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Provincia: {item.provincia || "-"}</div>
                  </td>
                  <td className="crm-postventa-alert-table-detail" data-label="Alerta">
                    <strong className="crm-postventa-alert-table-title">{item.tipoAlerta || "-"}</strong>
                    <div className="crm-postventa-alert-table-text">{item.resumen || "Sin descripción disponible."}</div>
                    <div className="crm-postventa-alert-table-subline">Motivo prioridad: {item.motivoPrioridad || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Motivo código: {item.motivoCodigo || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Tipo de gestión: {item.esAutomatica ? "Automática" : "Manual"}</div>
                    <div className="crm-postventa-alert-table-subline">Acción sugerida: {item.accionSugerida || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Mensaje sugerido: {item.mensajeSugerido || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Última actividad: {item.ultimaActividad || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Última entrega de rollos: {item.ultimaEntregaRollos || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Próximo contacto: {item.proximoContacto || "-"}</div>
                  </td>
                  <td data-label="Facturación">
                    <div className="crm-postventa-alert-table-subline">Hoy: {currency(item.montoHoy)}</div>
                    <div className="crm-postventa-alert-table-subline">Prom. 7 días: {currency(item.promedio7Dias)}</div>
                    <div className="crm-postventa-alert-table-subline">Variación: {percent(item.variacion)}</div>
                    <div className="crm-postventa-alert-table-subline">30 días: {currency(item.facturacion30Dias)}</div>
                    <div className="crm-postventa-alert-table-subline">Días con venta 30d: {item.diasConVenta30Dias || 0}</div>
                    <div className="crm-postventa-alert-table-subline">Vs patrón: {item.diasSinFacturarVsPatron || 0} día(s)</div>
                  </td>
                  <td data-label="Gestión">
                    <div className="crm-postventa-alert-table-subline">Última gestión: {item.ultimaGestion || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Responsable: {item.responsable || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Teléfono: {item.telefono || "-"}</div>
                  </td>
                  <td className="crm-postventa-action-cell" data-label="Acciones">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="crm-postventa-action-button"
                      onClick={() => setAlertaAccionItem(item)}
                    >
                      Acciones
                    </Button>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={6} className="text-center py-4 text-muted">
                    No hay alertas que coincidan con los filtros actuales.
                  </td>
                </tr>
              )}
            </tbody>
            </Table>
          </div>
        </div>
        </section>

        <Modal show={Boolean(alertaAccionItem)} onHide={() => setAlertaAccionItem(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Acciones de alerta</Modal.Title>
          </Modal.Header>
          <Modal.Body className="d-grid gap-2">
            <div className="crm-postventa-subtle p-0">
              {alertaAccionItem?.comercio || "Cliente"} · {alertaAccionItem?.tipoAlerta || "-"}
            </div>
            <div className="crm-postventa-subtle p-0">
              Acción sugerida: {alertaAccionItem?.accionSugerida || "-"}
            </div>
            <div className="crm-postventa-subtle p-0">
              Mensaje sugerido: {alertaAccionItem?.mensajeSugerido || "-"}
            </div>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionAlerta("verCliente", alertaAccionItem)}>
              Ver cliente
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionAlerta("copiarMensaje", alertaAccionItem)}>
              Copiar mensaje
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionAlerta("whatsapp", alertaAccionItem)}>
              Abrir WhatsApp
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionAlerta("registrarGestion", alertaAccionItem)}>
              Registrar gestión
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionAlerta("contactado", alertaAccionItem)}>
              Marcar como contactado
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionAlerta("posponer", alertaAccionItem)}>
              Posponer seguimiento
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionAlerta("cerrar", alertaAccionItem)}>
              Cerrar alerta
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionAlerta("escalar", alertaAccionItem)}>
              Escalar
            </Button>
          </Modal.Body>
        </Modal>

        <Modal show={Boolean(alertaSeleccionada)} onHide={() => setAlertaSeleccionada(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{alertaSeleccionada?.tipoAlerta || "Registrar gestión de alerta"}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={guardarGestionDesdeAlerta}>
            <Modal.Body>
              <p className="mb-3">{alertaSeleccionada?.comercio || "Cliente"} · {alertaSeleccionada?.cuit || "-"}</p>
              <div className="crm-postventa-form-grid">
                <Form.Group>
                  <Form.Label>Regla / acción</Form.Label>
                  <Form.Select
                    value={alertaGestionDraft.reglaId}
                    onChange={(event) => setAlertaGestionDraft((prev) => ({ ...prev, reglaId: event.target.value }))}
                  >
                    <option value="">Seleccionar</option>
                    {manualAlertaRules.map((regla) => (
                      <option key={regla.id} value={regla.id}>{regla.aliasVista} · {regla.nuevoEstado || "Sin estado"}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Estado</Form.Label>
                  <Form.Select
                    value={alertaGestionDraft.estadoGestion}
                    onChange={(event) => setAlertaGestionDraft((prev) => ({ ...prev, estadoGestion: event.target.value }))}
                  >
                    {(alertas.estadosGestion?.length ? alertas.estadosGestion : GESTION_STATES).map((item) => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Fecha / hora</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={alertaGestionDraft.fechaGestion}
                    onChange={(event) => setAlertaGestionDraft((prev) => ({ ...prev, fechaGestion: event.target.value }))}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Próximo contacto</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={alertaGestionDraft.fechaProximoContacto}
                    onChange={(event) => setAlertaGestionDraft((prev) => ({ ...prev, fechaProximoContacto: event.target.value }))}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Observación</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={alertaGestionDraft.observacion}
                    onChange={(event) => setAlertaGestionDraft((prev) => ({ ...prev, observacion: event.target.value }))}
                  />
                </Form.Group>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => setAlertaSeleccionada(null)}>
                Cancelar
              </Button>
              <Button type="submit" variant="success" disabled={cargas.guardandoAlertaGestion}>
                {cargas.guardandoAlertaGestion ? "Guardando..." : "Guardar gestión"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    );
  };

  const renderOrdenesPago = () => {
    if (cargas.ordenesPago) {
      return <div className="crm-postventa-subtle"><Spinner animation="border" size="sm" className="me-2" />Cargando órdenes de pago...</div>;
    }

    if (errores.ordenesPago) {
      return <Alert variant="danger">{errores.ordenesPago}</Alert>;
    }

    return (
      <div className="d-grid gap-3">
        <div className="crm-postventa-actions-row">
          <div className="crm-postventa-form-grid">
            <Form.Group>
              <Form.Label>Fecha consultada</Form.Label>
              <Form.Control
                type="date"
                value={ordenesPagoFiltros.fecha}
                onChange={(event) => setOrdenesPagoFiltros((prev) => ({ ...prev, fecha: event.target.value }))}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Buscar por CUIT o nombre documento</Form.Label>
              <Form.Control
                value={ordenesPagoFiltros.search}
                onChange={(event) => setOrdenesPagoFiltros((prev) => ({ ...prev, search: event.target.value }))}
                placeholder="CUIT o nombre documento"
              />
            </Form.Group>
          </div>
          <Button variant="outline-secondary" onClick={() => cargarOrdenesPago(clienteSeleccionadoId, ordenesPagoFiltros)}>
            Actualizar
          </Button>
        </div>

        <div className="crm-postventa-subtle">
          Fecha consultada: {ordenesPago?.fechaPago || ordenesPagoFiltros.fecha || "-"} · Resultado: {ordenesPago?.filas?.length || 0} documento(s)
        </div>

        <div className="crm-postventa-table-wrap">
          <Table className="crm-postventa-table mb-0" responsive hover>
            <thead>
              <tr>
                <th>ID</th>
                <th>Nombre documento</th>
                <th>CUIT</th>
                <th>Fecha subida</th>
                <th>Total bruto</th>
                <th>PDF</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {ordenesPago?.filas?.length ? (
                ordenesPago.filas.map((item) => (
                  <tr key={`${item.documentoId}-${item.cuit}`}>
                    <td>{item.documentoId}</td>
                    <td>{item.nombreDocumento || "-"}</td>
                    <td>{item.cuit || "-"}</td>
                    <td>{item.fechaSubida || "-"}</td>
                    <td>{formatCurrencyCell(item.totalBruto)}</td>
                    <td>{item.pdfDisponible ? "Disponible" : "-"}</td>
                    <td>
                      <Button variant="outline-secondary" size="sm" onClick={() => descargarOrdenPago(item)}>
                        Descargar PDF
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7">{ordenesPago?.message || "No hay órdenes de pago para mostrar."}</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
    );
  };

  const renderCupones = () => {
    if (cargas.cupones) {
      return <div className="crm-postventa-subtle"><Spinner animation="border" size="sm" className="me-2" />Cargando cupones...</div>;
    }

    if (errores.cupones) {
      return <Alert variant="danger">{errores.cupones}</Alert>;
    }

    return (
      <div className="d-grid gap-3">
        <div className="crm-postventa-actions-row">
          <div className="crm-postventa-form-grid">
            <Form.Group>
              <Form.Label>Mes</Form.Label>
              <Form.Control
                type="month"
                value={cuponesFiltros.mes}
                onChange={(event) => setCuponesFiltros((prev) => ({ ...prev, mes: event.target.value }))}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Buscador local</Form.Label>
              <Form.Control
                value={cuponesFiltros.search}
                onChange={(event) => setCuponesFiltros((prev) => ({ ...prev, search: event.target.value }))}
                placeholder="Cupón, tarjeta, comercio, CUIT o provincia"
              />
            </Form.Group>
          </div>
          <Button variant="outline-secondary" onClick={() => cargarCupones(clienteSeleccionadoId, cuponesFiltros)}>
            Actualizar
          </Button>
        </div>

        <div className="crm-postventa-subtle">
          Mes consultado: {cupones?.mesConsultado || formatLegacyMonthLabel(cuponesFiltros.mes) || "-"} | Resultado: {cupones?.filas?.length || 0} cupon(es)
        </div>

        <div className="crm-postventa-card-grid">
          <article className="crm-postventa-card">
            <span className="crm-postventa-card-label">Cantidad de cupones</span>
            <div className="crm-postventa-card-value">{cupones?.resumen?.cantidad ?? 0}</div>
          </article>
          <article className="crm-postventa-card">
            <span className="crm-postventa-card-label">Total bruto</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {formatCurrencyCell(cupones?.resumen?.totalBruto ?? 0)}
            </div>
          </article>
          <article className="crm-postventa-card">
            <span className="crm-postventa-card-label">Fecha operación</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {cupones?.resumen?.fechaOperacionMin || "-"} a {cupones?.resumen?.fechaOperacionMax || "-"}
            </div>
          </article>
          <article className="crm-postventa-card">
            <span className="crm-postventa-card-label">Fecha pago</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {cupones?.resumen?.fechaPagoMin || "-"} a {cupones?.resumen?.fechaPagoMax || "-"}
            </div>
          </article>
        </div>

        <div className="crm-postventa-subtle d-none">
          Mes consultado: {cupones?.mesConsultado || formatLegacyMonthLabel(cuponesFiltros.mes) || "-"} · Resultado: {cupones?.filas?.length || 0} cupon(es)
        </div>

        <div className="crm-postventa-subtle d-none">
          Rango: {cupones?.fechaDesde || cuponesFiltros.desde || "-"} a {cupones?.fechaHasta || cuponesFiltros.hasta || "-"} · Resultado: {cupones?.filas?.length || 0} cupón(es)
        </div>

        <div className="crm-postventa-table-wrap crm-postventa-table-wrap-scroll">
          <Table className="crm-postventa-table mb-0" responsive hover>
            <thead>
              <tr>
                <th>Fecha operación</th>
                <th>Fecha de pago</th>
                <th>N° cupón</th>
                <th>N° comercio</th>
                <th>N° tarjeta</th>
                <th>Bruto</th>
                <th>Cuotas</th>
                <th>Tarjeta</th>
                <th>Provincia</th>
                <th>Comercio</th>
                <th>CUIT</th>
              </tr>
            </thead>
            <tbody>
              {cupones?.filas?.length ? (
                cupones.filas.map((item) => (
                  <tr key={buildCuponDedupeKey(item)}>
                    <td>{item.fechaOperacion || "-"}</td>
                    <td>{item.fechaDePago || "-"}</td>
                    <td>{item.nroDeCupon || "-"}</td>
                    <td>{item.nroDeComercio || "-"}</td>
                    <td>{item.nroDeTarjeta || "-"}</td>
                    <td>{currency(item.totalBruto)}</td>
                    <td>{item.cuotas || "-"}</td>
                    <td>{item.tarjeta || "-"}</td>
                    <td>{item.provincia || "-"}</td>
                    <td>{item.nombreComercio || "-"}</td>
                    <td>{item.cuit || "-"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="11">{cupones?.message || "No hay cupones para mostrar."}</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
    );
  };

  const renderHerramientas = () => {
    const tools = [
      {
        title: "Simulador",
        description: "Acceso directo al simulador comercial actual del CRM.",
        path: "/administracion/AdminComparador",
      },
      {
        title: "Comparador",
        description: "Abrí el comparador actual sin duplicar lógica ni rutas.",
        path: "/administracion/AdminSimulador",
      },
      {
        title: "Simulador de órdenes",
        description: "Ingreso directo al simulador de liquidaciones / órdenes vigente.",
        path: "/admin/Simuladorliquidacion",
      },
    ];

    return (
      <div className="crm-postventa-tool-grid">
        {tools.map((tool) => (
          <article className="crm-postventa-tool-card" key={tool.path}>
            <span className="crm-postventa-card-label">Herramienta</span>
            <h4>{tool.title}</h4>
            <p>{tool.description}</p>
            <Button variant="success" onClick={() => navigate(tool.path)}>
              <FontAwesomeIcon className="me-2" icon={faArrowUpRightFromSquare} />
              Abrir
            </Button>
          </article>
        ))}
      </div>
    );
  };

  const renderGestion = () => {
    if (cargas.gestiones) {
      return <div className="crm-postventa-subtle"><Spinner animation="border" size="sm" className="me-2" />Cargando gestiones...</div>;
    }

    if (errores.gestiones) {
      return <Alert variant="danger">{errores.gestiones}</Alert>;
    }

    return (
      <div className="crm-postventa-gestion-grid">
        <section className={`crm-postventa-panel ${darkMode ? "is-dark" : ""}`}>
          <div className="crm-postventa-panel-title">
            <h3>Última gestión</h3>
          </div>
          {gestiones?.ultimaGestion ? (
            <DataList
              items={[
                { label: "Fecha y hora", value: gestiones.ultimaGestion.fechaHora },
                { label: "Usuario", value: gestiones.ultimaGestion.usuario },
                { label: "Motivo", value: gestiones.ultimaGestion.motivo },
                { label: "Motivo código", value: gestiones.ultimaGestion.motivoCodigo || "-" },
                { label: "Tipo", value: gestiones.ultimaGestion.esAutomatica ? "Automática" : (gestiones.ultimaGestion.tipo || "Manual") },
                {
                  label: "Prioridad",
                  value: gestiones.ultimaGestion.prioridad,
                  isBadge: true,
                  tone: toneForPriority(gestiones.ultimaGestion.prioridad),
                },
                {
                  label: "Estado",
                  value: gestiones.ultimaGestion.estado,
                  isBadge: true,
                  tone: toneForGestionState(gestiones.ultimaGestion.estado),
                },
                { label: "Descripción", value: gestiones.ultimaGestion.descripcion },
                { label: "Acción sugerida", value: gestiones.ultimaGestion.accionSugerida || "-" },
                { label: "Mensaje sugerido", value: gestiones.ultimaGestion.mensajeSugerido || "-" },
                { label: "Teléfono", value: gestiones.ultimaGestion.telefono || "-" },
                { label: "Próximo contacto", value: gestiones.ultimaGestion.proximoContacto },
              ]}
            />
          ) : (
            <Alert variant="secondary">Todavía no hay gestiones de postventa para este cliente.</Alert>
          )}

          {gestiones?.ultimaGestion ? (
            <div className="d-flex gap-2 flex-wrap mt-3">
              <Button variant="outline-secondary" onClick={() => copiarMensajeSugerido(gestiones.ultimaGestion, "Gestiones")}>
                Copiar mensaje
              </Button>
              <Button variant="outline-secondary" onClick={() => abrirWhatsappSugerido(gestiones.ultimaGestion, "Gestiones")}>
                Abrir WhatsApp
              </Button>
              <Button variant="outline-secondary" onClick={() => abrirAccionAlerta(gestiones.ultimaGestion, "Contactado", buildSuggestedObservation(gestiones.ultimaGestion, "Contactado"))}>
                Marcar como contactado
              </Button>
              <Button variant="outline-secondary" onClick={() => abrirAccionAlerta(gestiones.ultimaGestion, "Pendiente", buildSuggestedObservation(gestiones.ultimaGestion, "Pendiente"))}>
                Posponer
              </Button>
              <Button variant="outline-secondary" onClick={() => abrirAccionAlerta(gestiones.ultimaGestion, "Cerrado", buildSuggestedObservation(gestiones.ultimaGestion, "Cerrado"))}>
                Cerrar
              </Button>
              <Button variant="outline-secondary" onClick={() => abrirAccionAlerta(gestiones.ultimaGestion, "Escalado", buildSuggestedObservation(gestiones.ultimaGestion, "Escalado"))}>
                Escalar
              </Button>
            </div>
          ) : null}

          <div className="crm-postventa-rollos mt-4">
            <div className="crm-postventa-panel-title mb-3">
              <h4>Seguimiento de rollos de papel</h4>
              <Button variant="outline-secondary" size="sm" onClick={handleRegistrarRollo}>
                Registrar nueva entrega
              </Button>
            </div>
            <DataList
              items={[
                { label: "Ultima entrega", value: gestiones?.rollos?.ultimaEntrega || "-" },
                {
                  label: "Cantidad ultima entrega",
                  value: gestiones?.rollos?.cantidadUltimaEntrega > 0 ? String(gestiones.rollos.cantidadUltimaEntrega) : "-",
                },
                { label: "Observacion ultima entrega", value: gestiones?.rollos?.observacionUltimaEntrega || "-" },
              ]}
            />
            <div className="crm-postventa-table-wrap mt-3">
              <Table className="crm-postventa-table mb-0" responsive hover>
                <thead className="table-light">
                  <tr>
                    <th>Fecha</th>
                    <th>Cantidad</th>
                    <th>Usuario</th>
                    <th>Observacion</th>
                  </tr>
                </thead>
                <tbody>
                  {gestiones?.rollos?.historial?.length ? (
                    gestiones.rollos.historial.map((item) => (
                      <tr key={`rollo-${item.id}`}>
                        <td>{item.fechaEntrega}</td>
                        <td>{item.cantidad}</td>
                        <td>{item.usuario}</td>
                        <td>{item.observacion}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center py-4 text-muted">
                        No hay entregas registradas para este cliente.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </section>

        <section className={`crm-postventa-panel ${darkMode ? "is-dark" : ""}`}>
          <div className="crm-postventa-panel-title">
            <h3>Nueva gestión</h3>
          </div>
          <Form className="crm-postventa-form-grid" onSubmit={handleCrearGestion}>
            <Form.Group>
              <Form.Label>Fecha/hora</Form.Label>
              <Form.Control
                type="datetime-local"
                value={gestionDraft.fechaGestion}
                onChange={(event) =>
                  setGestionDraft((prev) => ({ ...prev, fechaGestion: event.target.value }))
                }
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Motivo / acción</Form.Label>
              <Form.Select
                value={gestionDraft.reglaId}
                onChange={(event) =>
                  setGestionDraft((prev) => ({ ...prev, reglaId: event.target.value }))
                }
              >
                <option value="">Seleccionar</option>
                {manualGestionRules.map((regla) => (
                  <option key={regla.id} value={regla.id}>
                    {regla.aliasVista} · {regla.nuevoEstado || "Sin estado"}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group>
              <Form.Label>Estado de gestion</Form.Label>
              <Form.Select
                value={gestionDraft.estadoGestion}
                onChange={(event) =>
                  setGestionDraft((prev) => ({ ...prev, estadoGestion: event.target.value }))
                }
              >
                {(gestiones?.estadosGestion?.length ? gestiones.estadosGestion : GESTION_STATES).map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group>
              <Form.Label>Proximo contacto</Form.Label>
              <Form.Control
                type="datetime-local"
                value={gestionDraft.fechaProximoContacto}
                onChange={(event) =>
                  setGestionDraft((prev) => ({ ...prev, fechaProximoContacto: event.target.value }))
                }
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Observación</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={gestionDraft.observacion}
                onChange={(event) =>
                  setGestionDraft((prev) => ({ ...prev, observacion: event.target.value }))
                }
                placeholder="Describí brevemente la interacción con el cliente."
              />
            </Form.Group>

            <div className="crm-postventa-subtle p-0">
              Usuario que atiende: {gestiones?.usuarioActual || "-"}
            </div>

            <div className="d-flex gap-2 flex-wrap">
              <Button type="submit" variant="success" disabled={cargas.guardandoGestion}>
                {cargas.guardandoGestion ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Guardando...
                  </>
                ) : (
                  "Guardar gestión"
                )}
              </Button>
              <Button
                type="button"
                variant="outline-secondary"
                onClick={() =>
                  setGestionDraft({
                    reglaId: manualGestionRules?.[0]?.id ? String(manualGestionRules[0].id) : "",
                    estadoGestion: "Pendiente",
                    observacion: "",
                    fechaGestion: `${makeToday()}T09:00`,
                    fechaProximoContacto: "",
                  })
                }
              >
                Limpiar
              </Button>
            </div>
          </Form>

          <div className="crm-postventa-table-wrap crm-postventa-table-wrap-scroll crm-postventa-table-wrap-safe mt-4">
            <Table className="crm-postventa-table mb-0" responsive hover>
              <thead className="table-light">
                <tr>
                  <th>Fecha y hora</th>
                  <th>Tipo</th>
                  <th>Prioridad</th>
                  <th>Usuario</th>
                  <th>Motivo</th>
                  <th>Acción sugerida</th>
                  <th>Mensaje sugerido</th>
                  <th>Estado</th>
                  <th>Próximo contacto</th>
                  <th>Observación</th>
                </tr>
              </thead>
              <tbody>
                {gestiones?.historial?.length ? (
                  gestiones.historial.map((item) => (
                    <tr key={item.id}>
                      <td>{item.fechaHora}</td>
                      <td>{item.tipo}</td>
                      <td>
                        <PostventaBadge value={item.prioridad} tone={toneForPriority(item.prioridad)} />
                      </td>
                      <td>{item.usuario}</td>
                      <td>{item.motivo}</td>
                      <td>{item.accionSugerida || "-"}</td>
                      <td>{item.mensajeSugerido || "-"}</td>
                      <td>
                        <PostventaBadge value={item.estado} tone={toneForGestionState(item.estado)} />
                      </td>
                      <td>{item.proximoContacto}</td>
                      <td>{item.observacion}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={10} className="text-center py-4 text-muted">
                      No hay historial de gestiones para mostrar.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

        <Modal show={rolloModalOpen} onHide={() => setRolloModalOpen(false)} centered>
          <Form onSubmit={handleGuardarRollo}>
            <Modal.Header closeButton>
              <Modal.Title>Registrar nueva entrega</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="crm-postventa-form-grid">
                <Form.Group>
                  <Form.Label>Fecha entrega</Form.Label>
                  <Form.Control
                    type="date"
                    value={rolloDraft.fechaEntrega}
                    onChange={(event) => setRolloDraft((prev) => ({ ...prev, fechaEntrega: event.target.value }))}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Cantidad</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    step="1"
                    value={rolloDraft.cantidad}
                    onChange={(event) => setRolloDraft((prev) => ({ ...prev, cantidad: event.target.value }))}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Observacion</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={rolloDraft.observacion}
                    onChange={(event) => setRolloDraft((prev) => ({ ...prev, observacion: event.target.value }))}
                    placeholder="Detalle opcional de la entrega."
                  />
                </Form.Group>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button type="button" variant="outline-secondary" onClick={() => setRolloModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" variant="success" disabled={cargas.guardandoRollo}>
                {cargas.guardandoRollo ? "Guardando..." : "Guardar"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
        </section>
      </div>
    );
  };

  const renderSeguimiento = () => {
    if (cargas.seguimiento) {
      return <div className="crm-postventa-subtle"><Spinner animation="border" size="sm" className="me-2" />Cargando seguimiento...</div>;
    }

    if (errores.seguimiento) {
      return <Alert variant="danger">{errores.seguimiento}</Alert>;
    }

    if (!seguimiento) {
      return <Alert variant="secondary">No hay seguimiento disponible para este cliente.</Alert>;
    }

    return (
      <div className="d-grid gap-4">
        {!seguimiento.tieneDatosReales && seguimiento.message ? (
          <Alert variant="secondary">{seguimiento.message}</Alert>
        ) : null}

        <div className="crm-postventa-detail-grid">
          <section className={`crm-postventa-panel ${darkMode ? "is-dark" : ""}`}>
            <div className="crm-postventa-panel-title">
              <h3>Hoja de vida del cliente</h3>
            </div>
            <div className="crm-postventa-timeline">
              {seguimiento.timeline?.length ? (
                seguimiento.timeline.map((item, index) => (
                  <article className="crm-postventa-timeline-item" key={`${item.fecha}-${index}`}>
                    <span className="crm-postventa-card-label">{item.fecha}</span>
                    <h4 className="mb-1">{item.titulo}</h4>
                    {item.usuario && item.usuario !== "-" ? (
                      <div className="crm-postventa-timeline-user">Usuario: {item.usuario}</div>
                    ) : null}
                    <p className="crm-postventa-timeline-summary">
                      {item.resumen || item.descripcion || "Sin resumen disponible."}
                    </p>
                    <div className="crm-postventa-timeline-actions">
                      <Button
                        variant="outline-success"
                        size="sm"
                        className="crm-postventa-timeline-button"
                        onClick={() => setTimelineDetalle(item)}
                      >
                        Ver detalle
                      </Button>
                    </div>
                  </article>
                ))
              ) : (
                <Alert variant="secondary">No hay eventos de seguimiento para mostrar.</Alert>
              )}
            </div>
          </section>

          <section className={`crm-postventa-panel ${darkMode ? "is-dark" : ""}`}>
            <div className="crm-postventa-panel-title">
              <h3>Métricas</h3>
            </div>
            <div className="crm-postventa-metric-grid">
              {seguimiento.metricas?.map((item) => (
                <article className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`} key={item.label}>
                  <span className="crm-postventa-card-label">{item.label}</span>
                  <div className="crm-postventa-card-value" style={{ fontSize: 19 }}>
                    {item.value}
                  </div>
                </article>
              ))}
            </div>
          </section>
        </div>

        <Modal
          show={Boolean(timelineDetalle)}
          onHide={() => setTimelineDetalle(null)}
          centered
          size="lg"
          className="crm-postventa-history-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>{timelineDetalle?.titulo || "Detalle del evento"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="crm-postventa-detail-meta">
              <div>
                <span className="crm-postventa-card-label">Fecha</span>
                <strong>{timelineDetalle?.fecha || "-"}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Tipo</span>
                <strong>{getTimelineTipoDetalle(timelineDetalle)}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Usuario</span>
                <strong>{timelineDetalle?.usuario || "-"}</strong>
              </div>
            </div>

            <div className="mt-3">
              <span className="crm-postventa-card-label">Resumen</span>
              <p className="mb-0">{timelineDetalle?.resumen || timelineDetalle?.descripcion || "-"}</p>
            </div>

            <div className="mt-4">
              <span className="crm-postventa-card-label">Observaciones completas</span>
              {timelineDetalle?.detalleLineas?.length ? (
                <ul className="crm-postventa-detail-lines">
                  {timelineDetalle.detalleLineas.map((linea, index) => (
                    <li key={`${timelineDetalle.fecha}-${index}`}>{linea}</li>
                  ))}
                </ul>
              ) : (
                <div className="crm-postventa-detail-pre">
                  {timelineDetalle?.detalle || timelineDetalle?.resumen || "Sin detalle disponible."}
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setTimelineDetalle(null)}>
              Cerrar
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  };

  const renderSection = () => {
    switch (section) {
      case "ficha":
        return renderFicha();
      case "facturacion":
        return renderFacturacion();
      case "alertas":
        return renderAlertas();
      case "ordenes-pago":
        return renderOrdenesPago();
      case "cupones":
        return renderCupones();
      case "herramientas":
        return renderHerramientas();
      case "gestion":
        return renderGestion();
      case "seguimiento":
        return renderSeguimiento();
      default:
        return renderAlertas();
    }
  };

  if (cargandoSesion) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
        <Spinner animation="border" />
      </div>
    );
  }

  const requiereClienteSeleccionado = section !== "alertas";
  const emptyStateTitle =
    section === "ordenes-pago"
      ? "Seleccioná un cliente para ver sus órdenes de pago."
      : section === "cupones"
        ? "Seleccioná un cliente para ver sus cupones."
        : "Seleccioná un cliente para ver la información";
  const emptyStateDescription =
    section === "ordenes-pago"
      ? "La consulta reutiliza las órdenes reales asociadas al cliente seleccionado en CRM Postventa."
      : section === "cupones"
        ? "El listado usa cupones reales del cliente seleccionado y respeta el mismo selector global."
        : "El módulo queda preparado sobre datos reales. Elegí un cliente/comercio para cargar ficha, facturación, herramientas, gestión y seguimiento.";
  const alertaHeader = {
    kicker: "CRM Postventa",
    title: "Centro de alertas",
    chips: [
      `Fecha ${alertas?.fechaReferencia || alertasFiltros.fecha || "-"}`,
      `Facturacion ${alertas?.alertasFacturacion?.length || 0}`,
      `Gestion ${alertas?.alertasGestion?.length || 0}`,
      `Criticas ${alertas?.alertasCriticas?.length || 0}`,
    ],
  };
  const panelTitle =
    section === "ficha"
      ? "Ficha del cliente"
      : section === "facturacion"
        ? "Facturacion"
        : section === "alertas"
          ? "Alertas"
          : section === "ordenes-pago"
            ? "Órdenes de pago"
            : section === "cupones"
              ? "Cupones"
          : section === "herramientas"
            ? "Herramientas"
            : section === "gestion"
              ? "Gestion"
              : "Seguimiento";

  return (
    <div className="crm-postventa-page" translate="no">
      <TituloPagina title="CRM Postventa" />

      <div className="container crm-postventa-shell py-4">
        <section className={`crm-postventa-topbar ${darkMode ? "is-dark" : ""}`}>
          <div className="crm-postventa-topbar-copy">
            <span className="crm-postventa-kicker">
              {section === "alertas" ? alertaHeader.kicker : clienteHeader.clienteSeleccionado || "Cliente seleccionado"}
            </span>
            <h2>
              {section === "alertas"
                ? alertaHeader.title
                : clienteHeader.nombreDeFantasia || clienteHeader.razonSocial || "Seleccioná un cliente"}
            </h2>
            <div className="crm-postventa-meta">
              {section === "alertas" ? (
                alertaHeader.chips.map((item) => (
                  <span className="crm-postventa-chip" key={item}>
                    {item}
                  </span>
                ))
              ) : (
                <>
                  <span className="crm-postventa-chip">CUIT {clienteHeader.cuit || "-"}</span>
                  <span className="crm-postventa-chip">Estado {clienteHeader.estado || "-"}</span>
                  <span className="crm-postventa-chip">Terminal {clienteHeader.nDeTerminal || "-"}</span>
                  <span className="crm-postventa-chip">Comercio {clienteHeader.nDeComercio || "-"}</span>
                  <span className="crm-postventa-chip">Legajo {clienteHeader.legajo || "-"}</span>
                </>
              )}
            </div>
          </div>

          <Button className="crm-postventa-cta" variant="success" onClick={() => setClienteModalOpen(true)}>
            <FontAwesomeIcon className="me-2" icon={faUserPen} />
            {section === "alertas" ? "Seleccionar cliente" : "Cambiar cliente"}
          </Button>
        </section>

        {!clienteSeleccionadoId && requiereClienteSeleccionado ? (
          <section className="crm-postventa-empty-state mt-4">
            <FontAwesomeIcon icon={faBuilding} size="2x" />
            <h3>{emptyStateTitle}</h3>
            <p className="mb-0">{emptyStateDescription}</p>
            <Button variant="success" onClick={() => setClienteModalOpen(true)}>
              Elegir cliente
            </Button>
          </section>
        ) : (
          <section className={`crm-postventa-panel mt-4 ${darkMode ? "is-dark" : ""}`}>
            {terminalesGrupo.length > 1 && section !== "alertas" ? (
              <div className="crm-postventa-terminal-switcher">
                <div className="crm-postventa-terminal-tabs">
                  {terminalesGrupo.map((terminal) => (
                    <button
                      key={terminal.clienteId}
                      type="button"
                      className={`crm-postventa-terminal-tab ${terminal.clienteId === clienteSeleccionadoId ? "is-active" : ""}`}
                      onClick={() => handleCambiarTerminal(terminal)}
                    >
                      <span>{terminal.label}</span>
                      <small>{terminal.resumen || "Sin datos"}</small>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="crm-postventa-panel-title">
              <h3>{panelTitle}</h3>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  if (section === "alertas") {
                    cargarAlertas(alertasFiltros);
                    return;
                  }

                  if (section === "ordenes-pago") {
                    cargarOrdenesPago(clienteSeleccionadoId, ordenesPagoFiltros);
                    return;
                  }

                  if (section === "cupones") {
                    cargarCupones(clienteSeleccionadoId, cuponesFiltros);
                    return;
                  }

                  cargarModulo(clienteSeleccionadoId, analyticsRange);
                }}
              >
                <FontAwesomeIcon className="me-2" icon={faArrowsRotate} />
                Recargar
              </Button>
            </div>
            {renderSection()}
          </section>
        )}
      </div>

      <div className="py-4 mt-auto">
        <Footer />
      </div>

      <ClienteSelectorModal
        show={clienteModalOpen}
        onHide={() => setClienteModalOpen(false)}
        search={clienteSearch}
        onSearchChange={setClienteSearch}
        loading={clienteLoading}
        results={clienteGrupos}
        selectedKey={currentGroupKey}
        onSelect={setClientePendiente}
        onConfirm={() => seleccionarCliente(clientePendiente || ficha?.header)}
      />
    </div>
  );
};

export default CrmPostventa;

